// Drop-in replacement for src/components/RetirementCalculator.jsx
// All state, handlers, and calculation logic preserved verbatim — only the
// presentation layer (JSX + Tailwind classes + small helper components) is new.

import React, { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useDisclaimer } from './DisclaimerModal';
import { SiteFooter } from './SiteLayout';
import ProjectionChart from './ProjectionChart';
import {
  createCalculateTax,
  computeFinancesForPotWithdrawal,
  solvePotWithdrawalForNetTarget,
  annuityGrossForAge,
  dbPensionGrossForAge,
  resolveAnnuityPurchase,
  annuityPurchasePercentFromAmount,
  formatAnnuityPurchasePercent,
  formatAnnuityIncomeRatePercent,
  resolveAnnuityAnnualGross,
  annuityIncomeRatePercentFromGross,
  ANNUITY_INCOME_RATE_MIN,
  ANNUITY_INCOME_RATE_MAX,
  computeCashBufferDrawForYear,
  maxPreRetirementBridgeYears,
  cashBufferPreRetirementLookbackCap,
  cashBufferBalanceAtAge,
  cashBufferDepletedAgeFromChartData,
} from '../retirementMath';
import { decodeShareParam, encodeShareParam } from '../shareState';
import {
  findOverrideForAge,
  normalizeIncomeOverrides,
  resolveTargetNetAtAge,
  validateDrawdownYears,
} from '../incomeOverrides';
import IncomeOverridesEditor, { customDrawdownYearsSummary } from './IncomeOverridesEditor';
import {
  buildSimulatedInflationPath,
  cumulativeInflationFactor,
  getNominalGrowthDecimal,
  withdrawalInflationFactor,
} from '../inflationPath';
import {
  ONS_CPIH_ANNUAL_RATE_URL,
  ONS_CPIH_SERIES_ID,
  ONS_CPIH_DATASET_ID,
} from '../inflationModel';

// ─── Presentational helpers ──────────────────────────────────────────────────

function computeLabelHintPosition(anchorRect, tooltipRect, preferredPlacement) {
  const gap = 8;
  const margin = 12;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const spaceAbove = anchorRect.top - margin;
  const spaceBelow = vh - anchorRect.bottom - margin;

  let placeBelow = preferredPlacement === 'below';
  if (preferredPlacement === 'above') {
    if (tooltipRect.height + gap > spaceAbove && spaceBelow >= spaceAbove) {
      placeBelow = true;
    }
  } else if (tooltipRect.height + gap > spaceBelow && spaceAbove > spaceBelow) {
    placeBelow = false;
  }

  let left = anchorRect.left + anchorRect.width / 2;
  const halfW = tooltipRect.width / 2;
  left = Math.max(margin + halfW, Math.min(vw - margin - halfW, left));

  if (placeBelow) {
    let top = anchorRect.bottom + gap;
    if (top + tooltipRect.height > vh - margin) {
      top = Math.max(margin, vh - margin - tooltipRect.height);
    }
    return { top, left, transform: 'translateX(-50%)' };
  }

  let top = anchorRect.top - gap;
  const boxTop = top - tooltipRect.height;
  if (boxTop < margin) {
    if (spaceBelow >= tooltipRect.height + gap) {
      let belowTop = anchorRect.bottom + gap;
      if (belowTop + tooltipRect.height > vh - margin) {
        belowTop = Math.max(margin, vh - margin - tooltipRect.height);
      }
      return { top: belowTop, left, transform: 'translateX(-50%)' };
    }
    top = margin + tooltipRect.height;
  }
  return { top, left, transform: 'translate(-50%, -100%)' };
}

const LabelHintTooltip = ({
  anchorRef,
  placement,
  tooltipClassName,
  positionTick,
  children,
}) => {
  const tipRef = React.useRef(null);
  const [style, setStyle] = React.useState({
    position: 'fixed',
    top: 0,
    left: 0,
    opacity: 0,
    pointerEvents: 'none',
    zIndex: 10000,
    transform: 'translateX(-50%)',
  });

  const updatePosition = React.useCallback(() => {
    const anchor = anchorRef.current;
    const tip = tipRef.current;
    if (!anchor || !tip) return;
    const pos = computeLabelHintPosition(
      anchor.getBoundingClientRect(),
      tip.getBoundingClientRect(),
      placement,
    );
    setStyle({
      position: 'fixed',
      pointerEvents: 'none',
      zIndex: 10000,
      opacity: 1,
      ...pos,
    });
  }, [anchorRef, placement]);

  React.useLayoutEffect(() => {
    updatePosition();
    const frame = requestAnimationFrame(updatePosition);
    return () => cancelAnimationFrame(frame);
  }, [updatePosition, positionTick]);

  return (
    <span
      ref={tipRef}
      role="tooltip"
      style={style}
      className={`block ${tooltipClassName} max-w-[min(300px,calc(100vw-2rem))] px-3 py-2.5 rounded-lg border border-slate-200 bg-white text-[11.5px] leading-snug text-slate-600 shadow-lg normal-case tracking-normal font-normal`}
    >
      {children}
    </span>
  );
};

const LabelHint = ({ text, children, placement = 'above', tooltipClassName = 'w-[248px]' }) => {
  const [visible, setVisible] = React.useState(false);
  const [positionTick, setPositionTick] = React.useState(0);
  const anchorRef = React.useRef(null);

  React.useEffect(() => {
    if (!visible) return undefined;
    const bump = () => setPositionTick((t) => t + 1);
    window.addEventListener('scroll', bump, true);
    window.addEventListener('resize', bump);
    return () => {
      window.removeEventListener('scroll', bump, true);
      window.removeEventListener('resize', bump);
    };
  }, [visible]);

  const tooltipContent = children ?? text;

  return (
    <>
      <span
        ref={anchorRef}
        className="relative inline-flex ml-1.5 align-middle"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        onMouseEnter={() => setVisible(true)}
        onMouseLeave={() => setVisible(false)}
        onFocus={() => setVisible(true)}
        onBlur={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget)) {
            setVisible(false);
          }
        }}
      >
        <button
          type="button"
          className="w-4 h-4 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100 flex items-center justify-center transition focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-1"
          aria-label="More information"
          onClick={(e) => e.stopPropagation()}
        >
          <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.3" />
            <path d="M8 7v4M8 5v.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
          </svg>
        </button>
      </span>
      {visible && typeof document !== 'undefined' && createPortal(
        <LabelHintTooltip
          anchorRef={anchorRef}
          placement={placement}
          tooltipClassName={tooltipClassName}
          positionTick={positionTick}
        >
          {tooltipContent}
        </LabelHintTooltip>,
        document.body,
      )}
    </>
  );
};

const AnnuityDetailsHint = ({ annuityPurchase, annuityAnnualGross, lifeExpectancy }) => (
  <LabelHint placement="below" tooltipClassName="w-[min(300px,calc(100vw-2rem))]">
    <span className="flex flex-col gap-2">
      <span>Annual gross income as % of purchase — get a quote from a provider</span>
      {annuityPurchase > 0 && annuityAnnualGross > 0 ? (
        <span className="tabular-nums text-slate-800 font-medium">
          £{Math.round(annuityPurchase).toLocaleString()} purchase → £{Math.round(annuityAnnualGross).toLocaleString()}/yr gross (level) until age {lifeExpectancy}
        </span>
      ) : (
        <span className="tabular-nums text-slate-500">
          Set purchase and income rate to see projected annual income to your plan age
        </span>
      )}
      <span className="text-slate-500">
        Planning assumption only: income to your plan age, not a provider quote. A real lifetime annuity usually pays for life. Level income does not rise with inflation.
      </span>
    </span>
  </LabelHint>
);

const EditableAmount = ({
  value,
  rawValue,
  onCommit,
  editable = true,
  suffix,
  min = 0,
  max,
  size = 'md',
}) => {
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState('');
  const inputRef = React.useRef(null);

  React.useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const begin = () => {
    if (!editable) return;
    setDraft(String(rawValue));
    setEditing(true);
  };
  const commit = () => {
    const n = Number(String(draft).replace(/[^\d.]/g, ''));
    if (!isNaN(n) && onCommit) {
      let v = n;
      if (min !== undefined) v = Math.max(min, v);
      if (max !== undefined) v = Math.min(max, v);
      onCommit(Math.round(v));
    }
    setEditing(false);
  };

  const textClass = size === 'sm'
    ? 'text-[13px] font-medium text-slate-900 tabular-nums'
    : 'text-[15px] font-medium text-slate-900 tabular-nums tracking-tight';

  if (editing) {
    return (
      <span className={`inline-flex items-baseline bg-white border border-slate-900 rounded-md px-2 py-0.5 leading-tight ${textClass}`}>
        <span className="text-slate-400 font-normal">£</span>
        <input
          ref={inputRef}
          type="text"
          inputMode="numeric"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
            if (e.key === 'Escape') setEditing(false);
          }}
          className="w-[88px] bg-transparent outline-none border-none text-right tabular-nums text-slate-900"
        />
        {suffix && <span className="text-slate-400 font-normal ml-0.5">{suffix}</span>}
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={begin}
      tabIndex={editable ? 0 : -1}
      className={`${textClass} bg-transparent border-none p-0 inline ${
        editable
          ? 'cursor-text px-1 -mx-1 rounded-sm underline decoration-dotted decoration-slate-400/90 underline-offset-[3px] hover:decoration-slate-700 hover:bg-slate-100/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300/80 focus-visible:ring-offset-1 transition-colors'
          : 'cursor-default'
      }`}
    >
      {value}
    </button>
  );
};

const EditableNumber = ({
  value,
  rawValue,
  onCommit,
  editable = true,
  min,
  max,
  size = 'sm',
}) => {
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState('');
  const inputRef = React.useRef(null);

  React.useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const begin = () => {
    if (!editable) return;
    setDraft(String(rawValue));
    setEditing(true);
  };
  const commit = () => {
    const n = parseInt(String(draft).replace(/[^\d]/g, ''), 10);
    if (!Number.isNaN(n) && onCommit) {
      let v = n;
      if (min !== undefined) v = Math.max(min, v);
      if (max !== undefined) v = Math.min(max, v);
      onCommit(v);
    }
    setEditing(false);
  };

  const textClass = size === 'sm'
    ? 'text-[13px] font-medium text-slate-900 tabular-nums'
    : 'text-[15px] font-medium text-slate-900 tabular-nums tracking-tight';

  if (editing) {
    return (
      <span className={`inline-flex items-baseline bg-white border border-slate-900 rounded-md px-2 py-0.5 leading-tight ${textClass}`}>
        <input
          ref={inputRef}
          type="text"
          inputMode="numeric"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
            if (e.key === 'Escape') setEditing(false);
          }}
          className="w-[40px] bg-transparent outline-none border-none text-center tabular-nums text-slate-900"
        />
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={begin}
      tabIndex={editable ? 0 : -1}
      className={`${textClass} bg-transparent border-none p-0 inline ${
        editable
          ? 'cursor-text px-1 -mx-1 rounded-sm underline decoration-dotted decoration-slate-400/90 underline-offset-[3px] hover:decoration-slate-700 hover:bg-slate-100/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300/80 focus-visible:ring-offset-1 transition-colors'
          : 'cursor-default'
      }`}
    >
      {value}
    </button>
  );
};

const FieldRow = ({ label, labelHint, value, children, help, editable, rawValue, onCommit, suffix, min, max }) => {
  return (
    <div className="py-4 border-t border-slate-200 first:border-t-0 first:pt-1">
      <div className="flex items-baseline justify-between gap-4 mb-3">
        <span className="text-[13.5px] font-medium text-slate-700 inline-flex items-center">
          {label}
          {labelHint && (
            typeof labelHint === 'string'
              ? <LabelHint text={labelHint} placement="below" tooltipClassName="w-[min(300px,calc(100vw-2rem))]" />
              : (
                <LabelHint placement="below" tooltipClassName="w-[min(300px,calc(100vw-2rem))]">
                  {labelHint}
                </LabelHint>
              )
          )}
        </span>
        <EditableAmount
          value={value}
          rawValue={rawValue}
          onCommit={onCommit}
          editable={editable}
          suffix={suffix}
          min={min}
          max={max}
        />
      </div>
      {children}
      {help && (
        <div className="mt-2 text-xs text-slate-500 leading-relaxed">{help}</div>
      )}
    </div>
  );
};

// Piecewise slider helpers — different step sizes in different value bands.
function piecewiseLayout(bands) {
  let cum = 0;
  const segs = bands.map((b) => {
    const count = Math.round((b.to - b.from) / b.step);
    const seg = { ...b, start: cum, end: cum + count };
    cum += count;
    return seg;
  });
  return { segs, total: cum };
}
function valueToIndex(layout, v) {
  for (const s of layout.segs) {
    if (v <= s.to) return s.start + Math.round((v - s.from) / s.step);
  }
  return layout.total;
}
function indexToValue(layout, i) {
  for (const s of layout.segs) {
    if (i <= s.end) return s.from + (i - s.start) * s.step;
  }
  return layout.segs[layout.segs.length - 1].to;
}
function valueToSliderPercent(layout, v) {
  if (layout.total <= 0) return 0;
  return (valueToIndex(layout, v) / layout.total) * 100;
}
const POT_BANDS = [
  { from: 0,       to: 25000,   step: 500    },
  { from: 25000,   to: 100000,  step: 2000   },
  { from: 100000,  to: 250000,  step: 5000   },
  { from: 250000,  to: 1000000, step: 10000  },
  { from: 1000000, to: 1800000, step: 40000  },
  { from: 1800000, to: 2600000, step: 100000 },
  { from: 2600000, to: 3000000, step: 200000 },
];
const CONTRIB_BANDS = [
  { from: 0,     to: 5000,   step: 100  },
  { from: 5000,  to: 25000,  step: 500  },
  { from: 25000, to: 100000, step: 1000 },
];
const WITHDRAWAL_SLIDER_MAX = 250000;
const WITHDRAWAL_BANDS = [
  { from: 0,      to: 12000,  step: 500 },
  { from: 12000,  to: 50000,  step: 1000 },
  { from: 50000,  to: 75000,  step: 2500 },
  { from: 75000,  to: 100000, step: 5000 },
  { from: 100000, to: 150000, step: 10000 },
  { from: 150000, to: WITHDRAWAL_SLIDER_MAX, step: 25000 },
];
const POT_LAYOUT = piecewiseLayout(POT_BANDS);
const CONTRIB_LAYOUT = piecewiseLayout(CONTRIB_BANDS);
const WITHDRAWAL_LAYOUT = piecewiseLayout(WITHDRAWAL_BANDS);

const CASH_BUFFER_MAX = 500000;
const CASH_BUFFER_DEFAULT_SPREAD_YEARS = 10;
const CASH_BUFFER_BANDS = [
  { from: 0,      to: 50000,   step: 1000  },
  { from: 50000,  to: 150000,  step: 5000  },
  { from: 150000, to: CASH_BUFFER_MAX, step: 10000 },
];
const CASH_BUFFER_LAYOUT = piecewiseLayout(CASH_BUFFER_BANDS);

const DEFAULT_POT_ACCESS_AGE = 55;
const POT_ACCESS_AGE_MIN = 50;
const POT_ACCESS_AGE_MAX = 75;
const STATE_PENSION_AGE_MIN = 65;
const STATE_PENSION_AGE_MAX = 72;

const CASH_BUFFER_MODE_BRIDGE = 'bridge';
const CASH_BUFFER_MODE_TAX = 'tax';
const CASH_BUFFER_MODE_CUSTOM = 'custom';
/** ISA / cash savings: no investment growth applied in buffer projections (for now). */
const CASH_BUFFER_NOMINAL_GROWTH_RATE = 0;

const SliderTicks = ({ ticks }) => (
  <div className="mt-2 flex justify-between text-[11px] text-slate-400 tabular-nums">
    {ticks.map((t, i) => <span key={i}>{t}</span>)}
  </div>
);

/** Axis labels at numeric positions (0–100), not evenly spaced. */
const SliderAxisTicks = ({ items }) => (
  <div className="relative mt-2 h-8 text-[11px] text-slate-400 leading-tight">
    {items.map(({ label, percent }, i) => {
      const atStart = percent <= 0;
      const atEnd = percent >= 100;
      return (
        <span
          key={i}
          className={`absolute top-0 max-w-[42%] tabular-nums ${
            atStart
              ? 'left-0 text-left'
              : atEnd
                ? 'right-0 text-right'
                : '-translate-x-1/2 text-center'
          }`}
          style={atStart || atEnd ? undefined : { left: `${percent}%` }}
        >
          {label}
        </span>
      );
    })}
  </div>
);

const Disclosure = ({ title, titleHint, summary, defaultOpen, children }) => {
  const [open, setOpen] = useState(!!defaultOpen);
  return (
    <div className="border-t border-slate-200 last:border-b">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-3.5 text-left group"
      >
        <span className="text-[13.5px] font-medium text-slate-900 tracking-tight inline-flex items-center flex-wrap gap-x-1">
          <span className="inline-flex items-center">
            {title}
            {titleHint}
          </span>
          {summary && (
            <span className="font-normal text-slate-500">· {summary}</span>
          )}
        </span>
        <span className={`text-[10px] text-slate-400 transition-transform ${open ? 'rotate-90' : ''}`}>▶</span>
      </button>
      <div className={`grid transition-[grid-template-rows] duration-300 ease-out ${open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
        <div className={`min-h-0 ${open ? 'overflow-visible' : 'overflow-hidden'}`}>
          <div className="pb-4">{children}</div>
        </div>
      </div>
    </div>
  );
};

const Kpi = ({ label, value, sub, tone = 'neutral', toneText, className = '' }) => {
  const toneStyles = {
    good:    { dot: 'bg-emerald-500', bg: 'bg-emerald-50',  text: 'text-emerald-700' },
    okay:    { dot: 'bg-sky-500',     bg: 'bg-sky-50',      text: 'text-sky-700' },
    warn:    { dot: 'bg-amber-500',   bg: 'bg-amber-50',    text: 'text-amber-700' },
    bad:     { dot: 'bg-rose-500',    bg: 'bg-rose-50',     text: 'text-rose-700' },
    neutral: { dot: 'bg-slate-400',   bg: 'bg-slate-100',   text: 'text-slate-600' },
  }[tone];
  return (
    <div className={`px-5 py-4 ${className}`}>
      <div className="text-[11px] font-medium tracking-wider uppercase text-slate-500">{label}</div>
      <div className="mt-2.5 text-[22px] font-medium text-slate-900 tabular-nums tracking-tight leading-none">{value}</div>
      <div className="mt-2 flex items-center gap-2 text-xs text-slate-500">
        {toneText && (
          <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium ${toneStyles.bg} ${toneStyles.text}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${toneStyles.dot}`} />
            {toneText}
          </span>
        )}
        {sub && <span>{sub}</span>}
      </div>
    </div>
  );
};

const formatCompactGbp = (amount) => {
  const n = Math.abs(amount);
  if (n >= 1_000_000) {
    const m = n / 1_000_000;
    return `£${m % 1 === 0 ? Math.round(m) : m.toFixed(1)}m`;
  }
  if (n >= 1000) return `£${Math.round(n / 1000)}k`;
  return `£${Math.round(amount).toLocaleString()}`;
};

const TaxFreeAllowanceKpi = ({
  lifetimeCap,
  potEntitlement,
  usedInPlan,
  unusedFromPot,
  usagePct,
  capBindsPot,
  taxFreeOption,
  exhaustionAge,
  lifeExpectancy,
  className = '',
}) => {
  const fullyUsed = unusedFromPot < 1000;
  let tone = 'neutral';
  let toneText = 'Partial use';

  if (taxFreeOption === 'lumpsum' && fullyUsed) {
    tone = 'good';
    toneText = 'Fully used at retirement';
  } else if (fullyUsed) {
    tone = 'good';
    toneText = exhaustionAge ? `Fully used by age ${exhaustionAge}` : 'Fully used in plan';
  } else if (unusedFromPot >= 10000) {
    tone = 'warn';
    toneText = 'Allowance unused';
  } else if (taxFreeOption === 'phased' && usagePct >= 15) {
    tone = 'good';
    toneText = 'Phased drawdown';
  } else if (usagePct >= 40) {
    tone = 'okay';
    toneText = 'Partial use';
  }

  const toneStyles = {
    good:    { dot: 'bg-emerald-500', bg: 'bg-emerald-50',  text: 'text-emerald-700', bar: 'bg-emerald-500' },
    okay:    { dot: 'bg-sky-500',     bg: 'bg-sky-50',      text: 'text-sky-700',     bar: 'bg-sky-500' },
    warn:    { dot: 'bg-amber-500',   bg: 'bg-amber-50',    text: 'text-amber-700',   bar: 'bg-amber-500' },
    neutral: { dot: 'bg-slate-400',   bg: 'bg-slate-100',   text: 'text-slate-600',   bar: 'bg-slate-500' },
  }[tone];

  const barPct = potEntitlement > 0 ? Math.min(100, (usedInPlan / potEntitlement) * 100) : 0;

  return (
    <div className={`relative z-10 px-5 py-4 flex flex-col overflow-visible ${className}`}>
      <div className="text-[11px] font-medium tracking-wider uppercase text-slate-500 inline-flex items-center">
        Tax-free allowance
        <LabelHint
          placement="below"
          text="Phased mode takes 25% tax-free from each pot withdrawal, summed over your drawdown years. “From pot” is 25% of your pot at retirement. Total used can exceed that if you draw for many years — each slice applies to inflating withdrawals while the pot may still be growing — until the lifetime cap. Higher net income depletes the pot sooner, so lifetime tax-free can fall even when each year’s slice is larger."
        />
      </div>
      <div className="mt-2.5 text-[22px] font-medium text-slate-900 tabular-nums tracking-tight leading-none">
        {formatCompactGbp(usedInPlan)}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-500">
        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium ${toneStyles.bg} ${toneStyles.text}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${toneStyles.dot}`} />
          {toneText}
        </span>
        <span className="tabular-nums">of {formatCompactGbp(potEntitlement)} from pot</span>
      </div>
      <div className="mt-3 h-1.5 w-full bg-slate-100 rounded-full overflow-hidden" aria-hidden="true">
        <div
          className={`h-full rounded-full transition-all ${toneStyles.bar}`}
          style={{ width: `${barPct}%` }}
        />
      </div>
      <div className="mt-2.5 grid grid-cols-2 gap-3 text-[10px] text-slate-500 leading-tight">
        <div>
          <div className="uppercase tracking-wide text-slate-400 mb-0.5">Lifetime cap</div>
          <div className="font-medium text-slate-700 tabular-nums">{formatCompactGbp(lifetimeCap)}</div>
        </div>
        <div>
          <div className="uppercase tracking-wide text-slate-400 mb-0.5">From pot</div>
          <div className="font-medium text-slate-700 tabular-nums">{formatCompactGbp(potEntitlement)}</div>
        </div>
      </div>
      {!fullyUsed && unusedFromPot >= 10000 && (
        <p className="mt-2.5 text-[11px] leading-snug text-amber-800 bg-amber-50/80 rounded-lg px-2.5 py-2">
          {formatCompactGbp(unusedFromPot)} may go unused by age {lifeExpectancy} at current withdrawal levels.
        </p>
      )}
      {capBindsPot && (
        <p className="mt-1.5 text-[10px] text-slate-400 leading-snug">
          Pot limit reached — lifetime cap applies across all pensions.
        </p>
      )}
      {!capBindsPot && potEntitlement < lifetimeCap && (
        <p className="mt-1.5 text-[10px] text-slate-400 leading-snug">
          Pot limit ({formatCompactGbp(potEntitlement)}) is below the lifetime cap.
        </p>
      )}
    </div>
  );
};

const INCOME_SPLIT_STATE_USER = 'oklch(0.62 0.13 250)';
const INCOME_SPLIT_STATE_PARTNER = 'oklch(0.72 0.11 280)';
const INCOME_SPLIT_DB = 'oklch(0.58 0.11 195)';

const IncomeSplitKpi = ({
  potSharePct,
  annuitySharePct,
  dbSharePct = 0,
  stateSharePct,
  userStateSharePct = 0,
  partnerStateSharePct = 0,
  includePartnerStatePension = false,
  cashBufferSharePct = 0,
  includeStatePension,
  hasStateIncome,
  hasDbIncome = false,
  hasAnnuityIncome,
  hasCashBufferIncome = false,
  className = '',
}) => {
  let tone = 'good';
  let toneText = 'Pot-led income';

  const hasFixedIncome = (includeStatePension && hasStateIncome) || hasDbIncome || hasAnnuityIncome;
  const hasGuaranteedIncome = hasFixedIncome || hasCashBufferIncome;
  const fixedIncomeSharePct = (includeStatePension && hasStateIncome ? stateSharePct : 0)
    + (hasDbIncome ? dbSharePct : 0)
    + (hasAnnuityIncome ? annuitySharePct : 0);

  if (!hasGuaranteedIncome) {
    tone = 'good';
    toneText = 'Self-funded';
  } else if (hasFixedIncome && fixedIncomeSharePct >= 55) {
    tone = 'bad';
    toneText = 'Heavy fixed-income reliance';
  } else if (fixedIncomeSharePct >= 40) {
    tone = 'warn';
    toneText = 'Fixed-income reliant';
  } else if (fixedIncomeSharePct >= 25) {
    tone = 'okay';
    toneText = 'Balanced mix';
  }

  const toneStyles = {
    good:    { dot: 'bg-emerald-500', bg: 'bg-emerald-50',  text: 'text-emerald-700', pot: 'bg-emerald-500', annuity: 'bg-violet-400', db: 'bg-teal-400', state: 'bg-emerald-300', buffer: 'bg-sky-400' },
    okay:    { dot: 'bg-sky-500',     bg: 'bg-sky-50',      text: 'text-sky-700',     pot: 'bg-sky-500',     annuity: 'bg-violet-400', db: 'bg-teal-400', state: 'bg-sky-300', buffer: 'bg-sky-400' },
    warn:    { dot: 'bg-amber-500',   bg: 'bg-amber-50',    text: 'text-amber-700',   pot: 'bg-emerald-500', annuity: 'bg-violet-400', db: 'bg-teal-400', state: 'bg-amber-500', buffer: 'bg-sky-400' },
    bad:     { dot: 'bg-rose-500',    bg: 'bg-rose-50',     text: 'text-rose-700',    pot: 'bg-emerald-500', annuity: 'bg-violet-400', db: 'bg-teal-400', state: 'bg-rose-500', buffer: 'bg-sky-400' },
    neutral: { dot: 'bg-slate-400',   bg: 'bg-slate-100',   text: 'text-slate-600',   pot: 'bg-slate-500',   annuity: 'bg-violet-400', db: 'bg-teal-400', state: 'bg-slate-300', buffer: 'bg-sky-400' },
  }[tone];

  const potBarPct = hasGuaranteedIncome ? potSharePct : 100;
  const annuityBarPct = hasAnnuityIncome ? annuitySharePct : 0;
  const dbBarPct = hasDbIncome ? dbSharePct : 0;
  const userStateBarPct = includeStatePension && hasStateIncome ? userStateSharePct : 0;
  const partnerStateBarPct = includePartnerStatePension && partnerStateSharePct > 0
    ? partnerStateSharePct
    : 0;
  const stateBarPct = includeStatePension && hasStateIncome
    ? (partnerStateBarPct > 0 ? userStateBarPct + partnerStateBarPct : stateSharePct)
    : 0;
  const cashBufferBarPct = hasCashBufferIncome ? cashBufferSharePct : 0;
  const showStateSplit = includePartnerStatePension && partnerStateBarPct > 0;
  const value = hasGuaranteedIncome
    ? `${Math.round(potSharePct)}% pot`
    : '100% pot';

  return (
    <div className={`px-5 py-4 flex flex-col ${className}`}>
      <div className="text-[11px] font-medium tracking-wider uppercase text-slate-500 inline-flex items-center">
        Income split
        <LabelHint
          placement="below"
          text="Share of lifetime gross income from pot withdrawals, cash buffer, and fixed streams (state pension, DB pension, annuity) through your plan age. Tone reflects combined fixed-income share, not pot drawdown alone."
        />
      </div>
      <div className="mt-2.5 text-[22px] font-medium text-slate-900 tabular-nums tracking-tight leading-none">
        {value}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-500">
        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium ${toneStyles.bg} ${toneStyles.text}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${toneStyles.dot}`} />
          {toneText}
        </span>
        {hasAnnuityIncome && (
          <span className="tabular-nums">{Math.round(annuitySharePct)}% annuity</span>
        )}
        {hasDbIncome && (
          <span className="tabular-nums">{Math.round(dbSharePct)}% DB pension</span>
        )}
        {hasCashBufferIncome && (
          <span className="tabular-nums">{Math.round(cashBufferSharePct)}% cash buffer</span>
        )}
        {includeStatePension && hasStateIncome && (
          <span className="tabular-nums">
            {showStateSplit
              ? (
                <>
                  {Math.round(stateSharePct)}% state (
                  <span style={{ color: INCOME_SPLIT_STATE_USER }}>{Math.round(userStateSharePct)}% yours</span>
                  {' · '}
                  <span style={{ color: INCOME_SPLIT_STATE_PARTNER }}>{Math.round(partnerStateSharePct)}% partner</span>
                  )
                </>
              )
              : `${Math.round(stateSharePct)}% state pension`}
          </span>
        )}
      </div>
      <div className="mt-3 h-1.5 w-full bg-slate-100 rounded-full overflow-hidden flex" aria-hidden="true">
        <div
          className={`h-full ${toneStyles.pot} transition-all`}
          style={{ width: `${potBarPct}%` }}
        />
        {annuityBarPct > 0 && (
          <div
            className={`h-full ${toneStyles.annuity} transition-all`}
            style={{ width: `${annuityBarPct}%` }}
          />
        )}
        {dbBarPct > 0 && (
          <div
            className="h-full transition-all"
            style={{ width: `${dbBarPct}%`, backgroundColor: INCOME_SPLIT_DB }}
          />
        )}
        {cashBufferBarPct > 0 && (
          <div
            className={`h-full ${toneStyles.buffer} transition-all`}
            style={{ width: `${cashBufferBarPct}%` }}
          />
        )}
        {stateBarPct > 0 && !showStateSplit && (
          <div
            className={`h-full ${toneStyles.state} transition-all`}
            style={{ width: `${stateBarPct}%` }}
          />
        )}
        {userStateBarPct > 0 && showStateSplit && (
          <div
            className="h-full transition-all"
            style={{ width: `${userStateBarPct}%`, backgroundColor: INCOME_SPLIT_STATE_USER }}
          />
        )}
        {partnerStateBarPct > 0 && (
          <div
            className="h-full transition-all"
            style={{ width: `${partnerStateBarPct}%`, backgroundColor: INCOME_SPLIT_STATE_PARTNER }}
          />
        )}
      </div>
      {hasGuaranteedIncome && (
        <div className="mt-2 flex justify-between text-[10px] text-slate-400 leading-tight gap-2">
          <span>Pot</span>
          {hasAnnuityIncome && <span className="text-center flex-1">Annuity</span>}
          {hasDbIncome && <span className="text-center flex-1">DB</span>}
          {hasCashBufferIncome && <span className="text-center flex-1">Buffer</span>}
          {includeStatePension && hasStateIncome && !showStateSplit && (
            <span className="text-right">State</span>
          )}
          {includeStatePension && hasStateIncome && showStateSplit && (
            <span className="text-right inline-flex items-center gap-2">
              <span className="inline-flex items-center gap-1">
                <span
                  className="w-1.5 h-1.5 rounded-full shrink-0"
                  style={{ backgroundColor: INCOME_SPLIT_STATE_USER }}
                  aria-hidden="true"
                />
                Yours
              </span>
              <span className="inline-flex items-center gap-1">
                <span
                  className="w-1.5 h-1.5 rounded-full shrink-0"
                  style={{ backgroundColor: INCOME_SPLIT_STATE_PARTNER }}
                  aria-hidden="true"
                />
                Partner
              </span>
            </span>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Main component ─────────────────────────────────────────────────────────

const RetirementCalculator = () => {
  // ───── State (unchanged) ──────────────────────────────────────────────────
  const [realGrowthRate, setRealGrowthRate] = useState(1);
  const [inflationRate, setInflationRate] = useState(3);
  const [useSimulatedInflation, setUseSimulatedInflation] = useState(false);
  const [inflationSimSeed, setInflationSimSeed] = useState(42);
  const [pensionContribution, setPensionContribution] = useState(15000);
  const [currentAge, setCurrentAge] = useState(40);
  const [retirementAge, setRetirementAge] = useState(65);
  const [currentPension, setCurrentPension] = useState(50000);

  const withdrawalSeededRef = React.useRef(false);
  const [withdrawalAmount, setWithdrawalAmount] = useState(0);
  const [userStatePensionAnnual, setUserStatePensionAnnual] = useState(12548);
  const [includeStatePensionInWithdrawal, setIncludeStatePensionInWithdrawal] = useState(true);
  const [includePartnerStatePension, setIncludePartnerStatePension] = useState(false);
  const [partnerStatePensionAnnual, setPartnerStatePensionAnnual] = useState(12548);
  const [taxFreeOption, setTaxFreeOption] = useState('phased');
  const [inflateWithdrawals, setInflateWithdrawals] = useState(true);
  const [assumptionsPanelOpen, setAssumptionsPanelOpen] = useState(false);
  const [dismissPotAccessRetirementNotice, setDismissPotAccessRetirementNotice] = useState(false);
  const [showCopiedToast, setShowCopiedToast] = useState(false);
  const { openDisclaimer } = useDisclaimer();
  const [showIncomeLines, setShowIncomeLines] = useState(true);
  const [chartFullscreenOpen, setChartFullscreenOpen] = useState(false);

  const [personalAllowance, setPersonalAllowance] = useState(12570);
  const [basicRateThreshold, setBasicRateThreshold] = useState(50270);
  const [higherRateThreshold, setHigherRateThreshold] = useState(125140);
  const [taxFreeLumpSumCap, setTaxFreeLumpSumCap] = useState(268275);
  const [fullStatePension, setFullStatePension] = useState(12548);
  const [statePensionAge, setStatePensionAge] = useState(68);
  const [partnerStatePensionAge, setPartnerStatePensionAge] = useState(68);
  const [lifeExpectancy, setLifeExpectancy] = useState(90);
  const [potAccessAge, setPotAccessAge] = useState(DEFAULT_POT_ACCESS_AGE);

  const [annuityEnabled, setAnnuityEnabled] = useState(false);
  const [annuityPurchasePercent, setAnnuityPurchasePercent] = useState(25);
  const [annuityPurchaseAmountExact, setAnnuityPurchaseAmountExact] = useState(null);
  const [annuityIncomeRatePercent, setAnnuityIncomeRatePercent] = useState(5);
  const [annuityAnnualGrossExact, setAnnuityAnnualGrossExact] = useState(null);

  const [dbPensionEnabled, setDbPensionEnabled] = useState(false);
  const [dbPensionAnnual, setDbPensionAnnual] = useState(15000);
  const [dbPensionAge, setDbPensionAge] = useState(65);

  const [cashBufferEnabled, setCashBufferEnabled] = useState(false);
  const [cashBufferMode, setCashBufferMode] = useState(CASH_BUFFER_MODE_CUSTOM);
  const [cashBufferAmount, setCashBufferAmount] = useState(0);
  const [cashBufferSpreadStartAge, setCashBufferSpreadStartAge] = useState(null);
  const [cashBufferSpreadEndAge, setCashBufferSpreadEndAge] = useState(null);
  const [incomeOverrides, setIncomeOverrides] = useState([]);

  const [validationErrors, setValidationErrors] = useState({
    personalAllowance: '',
    basicRateThreshold: '',
    higherRateThreshold: '',
    taxFreeLumpSumCap: '',
    fullStatePension: ''
  });

  const endAge = lifeExpectancy;
  const minRetirementAge = Math.max(potAccessAge, currentAge + 1);

  React.useEffect(() => {
    if (retirementAge < minRetirementAge) {
      setRetirementAge(minRetirementAge);
    }
  }, [minRetirementAge, retirementAge]);

  React.useEffect(() => {
    if (retirementAge !== potAccessAge) {
      setDismissPotAccessRetirementNotice(false);
    }
  }, [retirementAge, potAccessAge]);

  React.useEffect(() => {
    setDismissPotAccessRetirementNotice(false);
  }, [potAccessAge]);

  const cashBufferSpreadAgeMax = endAge;
  let cashBufferSpreadEndEffective = cashBufferSpreadEndAge ?? Math.min(
    retirementAge + CASH_BUFFER_DEFAULT_SPREAD_YEARS - 1,
    cashBufferSpreadAgeMax,
  );
  cashBufferSpreadEndEffective = Math.max(
    currentAge,
    Math.min(cashBufferSpreadAgeMax, cashBufferSpreadEndEffective),
  );

  // ───── URL state load (unchanged) ─────────────────────────────────────────
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const stateParam = params.get('s');

    if (stateParam) {
      try {
        const state = decodeShareParam(stateParam);
        if (!state) return;

        if (state.currentAge !== undefined) setCurrentAge(state.currentAge);
        if (state.retirementAge !== undefined) setRetirementAge(state.retirementAge);
        if (state.currentPension !== undefined) setCurrentPension(state.currentPension);
        if (state.pensionContribution !== undefined) setPensionContribution(state.pensionContribution);
        if (state.withdrawalAmount !== undefined) {
          setWithdrawalAmount(state.withdrawalAmount);
          withdrawalSeededRef.current = true;
        }
        if (state.realGrowthRate !== undefined) setRealGrowthRate(state.realGrowthRate);
        if (state.inflationRate !== undefined) setInflationRate(state.inflationRate);
        if (state.useSimulatedInflation !== undefined) setUseSimulatedInflation(state.useSimulatedInflation);
        if (state.inflationSimSeed !== undefined) setInflationSimSeed(state.inflationSimSeed);
        const fullSp = state.fullStatePension ?? 12548;
        if (state.userStatePensionAnnual !== undefined) {
          setUserStatePensionAnnual(state.userStatePensionAnnual);
        } else if (state.statePensionYears !== undefined) {
          setUserStatePensionAnnual(Math.round((state.statePensionYears / 35) * fullSp));
        }
        if (state.includeStatePensionInWithdrawal !== undefined) setIncludeStatePensionInWithdrawal(state.includeStatePensionInWithdrawal);
        if (state.includePartnerStatePension !== undefined) setIncludePartnerStatePension(state.includePartnerStatePension);
        if (state.partnerStatePensionAnnual !== undefined) {
          setPartnerStatePensionAnnual(state.partnerStatePensionAnnual);
        } else if (state.partnerStatePensionYears !== undefined) {
          setPartnerStatePensionAnnual(Math.round((state.partnerStatePensionYears / 35) * fullSp));
        }
        if (state.taxFreeOption !== undefined) setTaxFreeOption(state.taxFreeOption);
        if (state.inflateWithdrawals !== undefined) setInflateWithdrawals(state.inflateWithdrawals);
        if (state.personalAllowance !== undefined) setPersonalAllowance(state.personalAllowance);
        if (state.basicRateThreshold !== undefined) setBasicRateThreshold(state.basicRateThreshold);
        if (state.higherRateThreshold !== undefined) setHigherRateThreshold(state.higherRateThreshold);
        if (state.taxFreeLumpSumCap !== undefined) setTaxFreeLumpSumCap(state.taxFreeLumpSumCap);
        if (state.fullStatePension !== undefined) setFullStatePension(state.fullStatePension);
        if (state.statePensionAge !== undefined) setStatePensionAge(state.statePensionAge);
        if (state.partnerStatePensionAge !== undefined) {
          setPartnerStatePensionAge(state.partnerStatePensionAge);
        } else if (state.statePensionAge !== undefined) {
          setPartnerStatePensionAge(state.statePensionAge);
        }
        if (state.lifeExpectancy !== undefined) setLifeExpectancy(state.lifeExpectancy);
        if (state.potAccessAge !== undefined) setPotAccessAge(state.potAccessAge);
        if (state.annuityEnabled !== undefined) setAnnuityEnabled(state.annuityEnabled);
        if (state.annuityPurchasePercent !== undefined) setAnnuityPurchasePercent(state.annuityPurchasePercent);
        if (state.annuityPurchaseAmountExact !== undefined) {
          setAnnuityPurchaseAmountExact(state.annuityPurchaseAmountExact);
        } else if (state.annuityPurchaseAmount !== undefined) {
          setAnnuityPurchaseAmountExact(state.annuityPurchaseAmount);
        }
        if (state.annuityIncomeRatePercent !== undefined) setAnnuityIncomeRatePercent(state.annuityIncomeRatePercent);
        if (state.annuityAnnualGrossExact !== undefined) {
          setAnnuityAnnualGrossExact(state.annuityAnnualGrossExact);
        }
        if (state.dbPensionEnabled !== undefined) setDbPensionEnabled(state.dbPensionEnabled);
        if (state.dbPensionAnnual !== undefined) setDbPensionAnnual(state.dbPensionAnnual);
        if (state.dbPensionAge !== undefined) setDbPensionAge(state.dbPensionAge);
        if (state.cashBufferEnabled !== undefined) setCashBufferEnabled(state.cashBufferEnabled);
        if (state.cashBufferMode !== undefined) setCashBufferMode(state.cashBufferMode);
        if (state.cashBufferAmount !== undefined) setCashBufferAmount(state.cashBufferAmount);
        if (state.cashBufferSpreadStartAge !== undefined && state.cashBufferSpreadEndAge !== undefined) {
          setCashBufferSpreadStartAge(state.cashBufferSpreadStartAge);
          setCashBufferSpreadEndAge(state.cashBufferSpreadEndAge);
        } else if (state.cashBufferSpreadYears !== undefined && state.retirementAge !== undefined) {
          const curAge = state.currentAge ?? 45;
          const spreadMin = Math.max(
            curAge + 1,
            state.retirementAge - cashBufferPreRetirementLookbackCap(curAge, state.retirementAge),
          );
          const planAge = state.lifeExpectancy ?? 90;
          const start = Math.max(spreadMin, curAge + 1);
          const end = Math.min(start + state.cashBufferSpreadYears - 1, planAge);
          setCashBufferSpreadStartAge(start);
          setCashBufferSpreadEndAge(Math.max(start, end));
        }
        if (state.incomeOverrides !== undefined) {
          setIncomeOverrides(state.incomeOverrides);
        }
      } catch (e) {
        console.error('Failed to decode URL state:', e);
      }
    }
  }, []);

  React.useEffect(() => {
    if (!chartFullscreenOpen) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') setChartFullscreenOpen(false);
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [chartFullscreenOpen]);

  // ───── Share handler (unchanged) ──────────────────────────────────────────
  const handleShare = () => {
    const encoded = encodeShareParam({
      currentAge, retirementAge, currentPension, pensionContribution, withdrawalAmount,
      realGrowthRate, inflationRate, useSimulatedInflation, inflationSimSeed,
      userStatePensionAnnual, includeStatePensionInWithdrawal,
      includePartnerStatePension, partnerStatePensionAnnual,
      taxFreeOption, inflateWithdrawals, personalAllowance, basicRateThreshold,
      higherRateThreshold, taxFreeLumpSumCap, fullStatePension, statePensionAge, partnerStatePensionAge,
      lifeExpectancy,
      potAccessAge,
      annuityEnabled,
      annuityPurchasePercent,
      annuityPurchaseAmountExact,
      annuityIncomeRatePercent,
      annuityAnnualGrossExact,
      dbPensionEnabled,
      dbPensionAnnual,
      dbPensionAge,
      cashBufferEnabled,
      cashBufferMode,
      cashBufferAmount,
      cashBufferSpreadStartAge: cashBufferSpreadStartEffective,
      cashBufferSpreadEndAge: cashBufferSpreadEndEffective,
      incomeOverrides: normalizeIncomeOverrides(incomeOverrides, {
        currentAge, lifeExpectancy, retirementAge,
      }),
    });
    const url = `${window.location.origin}${window.location.pathname}?s=${encoded}`;

    const textarea = document.createElement('textarea');
    textarea.value = url;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();

    try {
      document.execCommand('copy');
      setShowCopiedToast(true);
      setTimeout(() => setShowCopiedToast(false), 3000);
    } catch (err) {
      prompt('Copy this results link (includes your pension inputs):', url);
    } finally {
      document.body.removeChild(textarea);
    }
  };

  // ───── Slider styling (restyled but injected the same way) ────────────────
  React.useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      /* Single-thumb sliders */
      input[type="range"].rc-slider {
        -webkit-appearance: none;
        appearance: none;
        width: 100%;
        height: 4px;
        background: #e2e8f0;
        border-radius: 999px;
        outline: none;
        margin: 6px 0;
      }
      input[type="range"].rc-slider::-webkit-slider-thumb {
        -webkit-appearance: none; appearance: none;
        width: 18px; height: 18px; border-radius: 50%;
        background: #ffffff; border: 1.5px solid #0f172a;
        box-shadow: 0 2px 6px rgba(0,0,0,0.10);
        cursor: pointer; transition: transform 0.12s ease;
      }
      input[type="range"].rc-slider::-webkit-slider-thumb:hover { transform: scale(1.1); }
      input[type="range"].rc-slider::-moz-range-thumb {
        width: 18px; height: 18px; border-radius: 50%;
        background: #ffffff; border: 1.5px solid #0f172a;
        box-shadow: 0 2px 6px rgba(0,0,0,0.10); cursor: pointer;
      }

      /* Dual-range sliders */
      .dual-range-slider {
        -webkit-appearance: none; appearance: none;
        background: transparent; pointer-events: none;
      }
      .dual-range-slider::-webkit-slider-thumb {
        -webkit-appearance: none; appearance: none;
        width: 18px; height: 18px; border-radius: 50%;
        background: #ffffff; border: 1.5px solid #0f172a;
        box-shadow: 0 2px 6px rgba(0,0,0,0.10);
        pointer-events: auto; cursor: pointer;
        margin-top: -8px; transition: transform 0.12s ease;
      }
      .dual-range-slider::-webkit-slider-thumb:hover { transform: scale(1.1); }
      .dual-range-slider::-moz-range-thumb {
        width: 18px; height: 18px; border-radius: 50%;
        background: #ffffff; border: 1.5px solid #0f172a;
        box-shadow: 0 2px 6px rgba(0,0,0,0.10);
        pointer-events: auto; cursor: pointer;
      }
      .dual-range-slider::-webkit-slider-runnable-track { background: transparent; height: 2px; }
      .dual-range-slider::-moz-range-track { background: transparent; height: 2px; }

      /* Tax-band tinted track for target-income slider */
      input[type="range"].rc-slider-tax {
        background: linear-gradient(
          to right,
          #ecfdf5 0%,
          #ecfdf5 var(--pa-pct, 12.57%),
          #fef3c7 var(--pa-pct, 12.57%),
          #fef3c7 var(--br-pct, 50.27%),
          #ffe4e6 var(--br-pct, 50.27%),
          #ffe4e6 100%
        ) !important;
      }

      /* Number inputs in assumptions panel — leaves room for £ prefix */
      .rc-num-input {
        appearance: none; background: #ffffff;
        border: 1px solid #e2e8f0; border-radius: 8px;
        padding: 8px 12px 8px 28px; font-size: 13px; color: #0f172a;
        font-variant-numeric: tabular-nums; outline: none; width: 100%;
        transition: border-color 0.15s ease;
      }
      .rc-num-input:focus { border-color: #0f172a; }
      .rc-num-input.error { border-color: #f43f5e; background: #fff1f2; }

      /* Custom checkbox + radio */
      .rc-checkbox, .rc-radio {
        appearance: none; -webkit-appearance: none;
        width: 16px; height: 16px; flex-shrink: 0;
        border: 1.5px solid #cbd5e1; background: #ffffff;
        cursor: pointer; transition: all 0.15s ease;
        position: relative; margin-top: 2px;
      }
      .rc-checkbox { border-radius: 4px; }
      .rc-radio { border-radius: 50%; }
      .rc-checkbox:checked, .rc-radio:checked { background: #0f172a; border-color: #0f172a; }
      .rc-checkbox:checked::after {
        content: ''; position: absolute; top: 2px; left: 4px;
        width: 5px; height: 9px;
        border-right: 1.5px solid #fff; border-bottom: 1.5px solid #fff;
        transform: rotate(45deg);
      }
      .rc-radio:checked::after {
        content: ''; position: absolute; top: 3px; left: 3px;
        width: 7px; height: 7px; border-radius: 50%; background: #fff;
      }

      @keyframes rc-fade-in { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }
      .rc-fade-in { animation: rc-fade-in 0.2s ease-out; }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  // ───── Tax + projection logic ─────────────────────────────────────────────
  const calculateTax = createCalculateTax({
    personalAllowance,
    basicRateThreshold,
    higherRateThreshold,
  });

  const nominalGrowthRate = (realGrowthRate + inflationRate) / 100;
  const baseCalendarYear = new Date().getFullYear();

  const inflationSim = useMemo(() => {
    if (!useSimulatedInflation) return null;
    return buildSimulatedInflationPath({
      endAge,
      currentAge,
      seed: inflationSimSeed,
      baseCalendarYear,
    });
  }, [useSimulatedInflation, endAge, currentAge, inflationSimSeed, baseCalendarYear]);

  const inflationPathMap = inflationSim?.pathMap ?? null;

  const inflationFactorAtYearsFromNow = (yearsFromNow) => cumulativeInflationFactor({
    yearsFromNow,
    pathMap: inflationPathMap,
    flatInflationPct: inflationRate,
    baseCalendarYear,
  });

  const withdrawalTargetMultiplierAtAge = (age) => withdrawalInflationFactor({
    age,
    retirementAge,
    currentAge,
    pathMap: inflationPathMap,
    flatInflationPct: inflationRate,
    inflateWithdrawals,
    baseCalendarYear,
  });

  const nominalGrowthDecimalForAge = (age) => {
    const growthCalendarYear = baseCalendarYear + (age - currentAge) + 1;
    return getNominalGrowthDecimal(
      realGrowthRate,
      inflationPathMap,
      growthCalendarYear,
      inflationRate,
    );
  };

  const pensionNominalAtAge = (targetAge) => {
    let pot = currentPension;
    for (let age = currentAge; age < targetAge; age++) {
      pot = pot * (1 + nominalGrowthDecimalForAge(age)) + pensionContribution;
    }
    return pot;
  };

  const inflationSummaryLabel = useSimulatedInflation
    ? `simulated CPIH (~${inflationSim?.summary.mean ?? inflationSim?.params.mean}% avg)`
    : `${inflationRate.toFixed(1)}% inflation`;
  const statePensionParams = {
    statePensionAnnual: userStatePensionAnnual,
    partnerStatePensionAnnual,
    includePartnerStatePension,
    statePensionAge,
    partnerStatePensionAge,
  };

  const dbPensionParams = {
    dbPensionAnnual: dbPensionEnabled ? dbPensionAnnual : 0,
    dbPensionAge,
    lifeExpectancy: endAge,
  };

  const userStatePensionAtAge = (age) => (
    includeStatePensionInWithdrawal && age >= statePensionAge ? userStatePensionAnnual : 0
  );
  const partnerStatePensionAtAge = (age) => (
    includeStatePensionInWithdrawal && includePartnerStatePension && age >= partnerStatePensionAge
      ? partnerStatePensionAnnual
      : 0
  );
  const dbPensionAtAge = (age) => dbPensionGrossForAge({
    age,
    dbPensionAge,
    dbPensionAnnual: dbPensionEnabled ? dbPensionAnnual : 0,
    lifeExpectancy: endAge,
  });
  const includeGuaranteedInTarget = (age) => (
    includeStatePensionInWithdrawal || dbPensionAtAge(age) > 0
  );

  const drawdownYearsValidation = useMemo(
    () => validateDrawdownYears(incomeOverrides, { retirementAge, lifeExpectancy }),
    [incomeOverrides, retirementAge, lifeExpectancy],
  );

  const effectiveIncomeOverrides = drawdownYearsValidation.effectiveRows;

  const getTargetNetForAge = (age) => resolveTargetNetAtAge({
    age,
    baseTarget: withdrawalAmount,
    inflateWithdrawals,
    retirementAge,
    currentAge,
    overrides: effectiveIncomeOverrides,
    pathMap: inflationPathMap,
    flatInflationPct: inflationRate,
    baseCalendarYear,
  });

  React.useEffect(() => {
    setIncomeOverrides((prev) => {
      if (!prev.length) return prev;
      let changed = false;
      const next = prev.map((row) => {
        let fromAge = row.fromAge;
        let toAge = row.toAge;
        if (fromAge < retirementAge) {
          fromAge = retirementAge;
          changed = true;
        }
        if (toAge > lifeExpectancy) {
          toAge = lifeExpectancy;
          changed = true;
        }
        if (fromAge > toAge) {
          fromAge = Math.min(retirementAge, lifeExpectancy);
          toAge = fromAge;
          changed = true;
        }
        if (fromAge === row.fromAge && toAge === row.toAge) return row;
        return { ...row, fromAge, toAge };
      });
      return changed ? next : prev;
    });
  }, [retirementAge, lifeExpectancy]);

  const cashBufferSpreadEndForFeasibility = (() => {
    if (cashBufferMode === CASH_BUFFER_MODE_BRIDGE) {
      return Math.min(cashBufferSpreadAgeMax, Math.max(currentAge, retirementAge - 1));
    }
    if (cashBufferMode === CASH_BUFFER_MODE_TAX) {
      return cashBufferSpreadAgeMax;
    }
    return cashBufferSpreadEndEffective;
  })();

  const cashBufferEarliestAge = currentAge + 1;
  const cashBufferPreRetLookbackCap = cashBufferPreRetirementLookbackCap(currentAge, retirementAge);

  const cashBufferMaxPreRetYears = cashBufferEnabled && cashBufferAmount > 0
    ? maxPreRetirementBridgeYears({
      initialBalance: cashBufferAmount,
      retirementAge,
      currentAge,
      spreadEndAge: cashBufferSpreadEndForFeasibility,
      getTargetNetForAge: getTargetNetForAge,
      nominalGrowthRate: CASH_BUFFER_NOMINAL_GROWTH_RATE,
      maxLookback: cashBufferPreRetLookbackCap,
    })
    : 0;

  const cashBufferSpreadAgeMin = Math.max(
    cashBufferEarliestAge,
    retirementAge - cashBufferMaxPreRetYears,
  );
  let cashBufferSpreadStartEffective = cashBufferSpreadStartAge ?? retirementAge;
  cashBufferSpreadStartEffective = Math.max(
    cashBufferSpreadAgeMin,
    Math.min(cashBufferSpreadAgeMax, cashBufferSpreadStartEffective),
  );
  if (cashBufferSpreadEndEffective < cashBufferSpreadStartEffective) {
    cashBufferSpreadEndEffective = cashBufferSpreadStartEffective;
  }
  const cashBufferSpreadYearsEffective = cashBufferSpreadEndEffective - cashBufferSpreadStartEffective + 1;
  const cashBufferStartOffset = cashBufferSpreadStartEffective - retirementAge;
  const cashBufferEndOffset = cashBufferSpreadEndEffective - retirementAge;
  const cashBufferOffsetMin = -cashBufferMaxPreRetYears;
  const cashBufferOffsetMax = cashBufferSpreadAgeMax - retirementAge;
  const cashBufferOffsetSpan = Math.max(1, cashBufferOffsetMax - cashBufferOffsetMin);
  const cashBufferOffsetToPct = (offset) =>
    ((offset - cashBufferOffsetMin) / cashBufferOffsetSpan) * 100;
  const cashBufferStartOffsetPct = cashBufferOffsetToPct(cashBufferStartOffset);
  const cashBufferEndOffsetPct = cashBufferOffsetToPct(cashBufferEndOffset);

  const cashBufferSpreadStartModeEffective = (() => {
    if (cashBufferMode === CASH_BUFFER_MODE_BRIDGE) {
      return Math.max(cashBufferEarliestAge, retirementAge - cashBufferMaxPreRetYears);
    }
    if (cashBufferMode === CASH_BUFFER_MODE_TAX) {
      return retirementAge;
    }
    return cashBufferSpreadStartEffective;
  })();

  const cashBufferSpreadEndModeEffective = (() => {
    if (cashBufferMode === CASH_BUFFER_MODE_BRIDGE) {
      return Math.min(
        cashBufferSpreadAgeMax,
        Math.max(cashBufferSpreadStartModeEffective, retirementAge - 1),
      );
    }
    if (cashBufferMode === CASH_BUFFER_MODE_TAX) {
      return cashBufferSpreadAgeMax;
    }
    return cashBufferSpreadEndEffective;
  })();

  const generateData = () => {
    const data = [];
    const isTaxMode = cashBufferMode === CASH_BUFFER_MODE_TAX;
    const spreadStart = cashBufferSpreadStartModeEffective;
    const spreadEnd = cashBufferSpreadEndModeEffective;
    let cashBufferBalance = cashBufferEnabled ? cashBufferAmount : 0;

    const applyCashBufferDrawForAge = (age) => {
      if (isTaxMode) return 0;
      if (!cashBufferEnabled || age < spreadStart || age > spreadEnd || cashBufferBalance <= 0) {
        return 0;
      }
      const projected = computeCashBufferDrawForYear({
        balance: cashBufferBalance,
        nominalGrowthRate: CASH_BUFFER_NOMINAL_GROWTH_RATE,
        targetNet: getTargetNetForAge(age),
        age,
        retirementAge,
        spreadEndAge: spreadEnd,
      });
      cashBufferBalance = projected.balanceAfter;
      return projected.draw;
    };

    const cashBufferBalanceAfterYear = () => (
      cashBufferEnabled ? Math.round(Math.max(0, cashBufferBalance)) : null
    );

    for (let age = currentAge; age < retirementAge; age++) {
      const yearsFromNow = age - currentAge;
      const pensionNominal = pensionNominalAtAge(age);
      const inflationFactor = inflationFactorAtYearsFromNow(yearsFromNow);
      const pensionReal = pensionNominal / inflationFactor;
      const yearNominalGrowth = nominalGrowthDecimalForAge(age);
      const cashBufferThisYear = applyCashBufferDrawForAge(age);
      const bridgeTargetNet = getTargetNetForAge(age);
      const bridgeCustomTarget = !!findOverrideForAge(effectiveIncomeOverrides, age);
      const chartBufferBalance = cashBufferEnabled && age >= spreadStart && age <= spreadEnd
        ? cashBufferBalanceAfterYear()
        : null;
      const potEndNominal = pensionNominal * (1 + yearNominalGrowth) + pensionContribution;
      data.push({
        age, phase: 'accumulation',
        totalNominal: Math.round(pensionNominal),
        totalReal: Math.round(pensionReal),
        potEndNominal: Math.round(potEndNominal),
        potGrowth: Math.round(pensionNominal * yearNominalGrowth),
        potContribution: Math.round(pensionContribution),
        potDeductionLumpSum: 0,
        potDeductionAnnuity: 0,
        potDeductionWithdrawal: 0,
        withdrawal: null, statePension: 0, userStatePension: 0, partnerStatePension: 0, dbPension: 0, annuity: 0,
        cashBuffer: Math.round(cashBufferThisYear),
        cashBufferBalance: chartBufferBalance,
        totalIncome: cashBufferThisYear > 0 ? Math.round(bridgeTargetNet) : null,
        taxPaid: cashBufferThisYear > 0 ? 0 : null,
        netIncome: cashBufferThisYear > 0 ? Math.round(bridgeTargetNet) : null,
        targetNet: Math.round(bridgeTargetNet),
        customTarget: bridgeCustomTarget,
      });
    }

    let totalAtRetirement;
    if (data.length > 0) {
      const lastAccumulationData = data[data.length - 1];
      const yearNominalGrowth = nominalGrowthDecimalForAge(retirementAge - 1);
      totalAtRetirement = lastAccumulationData.totalNominal * (1 + yearNominalGrowth) + pensionContribution;
    } else {
      totalAtRetirement = currentPension;
    }
    let currentTotal = totalAtRetirement;

    const taxFreeLumpSum = taxFreeOption === 'lumpsum' ? Math.min(totalAtRetirement * 0.25, taxFreeLumpSumCap) : 0;
    const potAtRetirementBeforeLumpSum = currentTotal;
    if (taxFreeOption === 'lumpsum') currentTotal = currentTotal - taxFreeLumpSum;
    const potAtRetirementBeforeWithdrawals = currentTotal;

    const annuityPurchase = resolveAnnuityPurchase({
      annuityEnabled,
      annuityPurchasePercent,
      annuityPurchaseAmountExact,
      postLumpPot: potAtRetirementBeforeWithdrawals,
    });
    const annuityAnnualGross = resolveAnnuityAnnualGross({
      annuityEnabled,
      annuityPurchase,
      annuityIncomeRatePercent,
      annuityAnnualGrossExact,
    });
    currentTotal = Math.max(0, currentTotal - annuityPurchase);

    let cumulativeTaxFreeWithdrawn = 0;
    let cumulativeGrowthDuringRetirement = 0;
    let cashBufferBalanceAtPlanAge = cashBufferEnabled ? Math.round(Math.max(0, cashBufferBalance)) : 0;
    let zeroTaxYearsAchieved = 0;
    const chartEndAge = endAge + 5;

    const findMaxNoTaxWithdrawal = ({ age, annuityThisYear, cumulativeTaxFreeWithdrawnThisYear }) => {
      const baseParams = {
        ...statePensionParams,
        ...dbPensionParams,
        annuityAnnual: annuityThisYear,
        age,
        statePensionAge,
        includeStatePensionInWithdrawal,
        taxFreeOption,
        cumulativeTaxFreeWithdrawn: cumulativeTaxFreeWithdrawnThisYear,
        taxFreeLumpSumCap,
        calculateTax,
        lumpSumThisYear: 0,
        cashBufferAnnual: 0,
      };

      const taxPaidFor = (potWithdrawal) => computeFinancesForPotWithdrawal({ ...baseParams, potWithdrawal }).taxPaid;

      let low = 0;
      let high = 1000;
      while (taxPaidFor(high) === 0 && high < 1_000_000) high *= 2;
      if (taxPaidFor(high) === 0) return high;

      for (let i = 0; i < 30; i++) {
        const mid = (low + high) / 2;
        if (taxPaidFor(mid) === 0) low = mid;
        else high = mid;
      }
      return low;
    };

    for (let age = retirementAge; age <= chartEndAge; age++) {
      const yearsFromNow = age - currentAge;

      let targetNet = getTargetNetForAge(age);
      const customTarget = !!findOverrideForAge(effectiveIncomeOverrides, age);

      const lumpSumThisYear = (age === retirementAge && taxFreeOption === 'lumpsum') ? taxFreeLumpSum : 0;
      const annuityThisYear = annuityGrossForAge({
        age,
        retirementAge,
        lifeExpectancy: endAge,
        annuityAnnualGross,
      });
      const dbPensionThisYear = dbPensionAtAge(age);

      let cashBufferThisYear = applyCashBufferDrawForAge(age);
      let forceNoTaxPotWithdrawal = null;
      if (
        isTaxMode
        && cashBufferEnabled
        && age >= spreadStart
        && age <= spreadEnd
        && cashBufferBalance > 0
      ) {
        const maxNoTaxPotWithdrawal = findMaxNoTaxWithdrawal({
          age,
          annuityThisYear,
          cumulativeTaxFreeWithdrawnThisYear: cumulativeTaxFreeWithdrawn,
        });

        const financesNoTax = computeFinancesForPotWithdrawal({
          potWithdrawal: maxNoTaxPotWithdrawal,
          ...statePensionParams,
          ...dbPensionParams,
          annuityAnnual: annuityThisYear,
          age,
          statePensionAge,
          includeStatePensionInWithdrawal,
          taxFreeOption,
          cumulativeTaxFreeWithdrawn,
          taxFreeLumpSumCap,
          calculateTax,
          lumpSumThisYear: 0,
          cashBufferAnnual: 0,
        });

        const netAtNoTax = includeGuaranteedInTarget(age)
          ? financesNoTax.recurringNet
          : financesNoTax.potOnlyNet;
        const topUpNeeded = Math.max(0, targetNet - netAtNoTax);
        cashBufferThisYear = Math.min(cashBufferBalance, topUpNeeded);
        cashBufferBalance = Math.max(0, cashBufferBalance - cashBufferThisYear);
        if (topUpNeeded > 0 && cashBufferThisYear >= topUpNeeded - 0.01) {
          // We can keep tax at £0 and still hit the target net (using ISA top-up).
          forceNoTaxPotWithdrawal = maxNoTaxPotWithdrawal;
        }
      }

      const solved = forceNoTaxPotWithdrawal != null
        ? computeFinancesForPotWithdrawal({
          potWithdrawal: forceNoTaxPotWithdrawal,
          ...statePensionParams,
          ...dbPensionParams,
          annuityAnnual: annuityThisYear,
          age,
          statePensionAge,
          includeStatePensionInWithdrawal,
          taxFreeOption,
          cumulativeTaxFreeWithdrawn,
          taxFreeLumpSumCap,
          calculateTax,
          lumpSumThisYear: 0,
          cashBufferAnnual: cashBufferThisYear,
        })
        : solvePotWithdrawalForNetTarget({
          targetNet,
          age,
          statePensionAge,
          ...statePensionParams,
          ...dbPensionParams,
          annuityAnnual: annuityThisYear,
          includeStatePensionInWithdrawal,
          taxFreeOption,
          cumulativeTaxFreeWithdrawn,
          taxFreeLumpSumCap,
          calculateTax,
          cashBufferAnnual: cashBufferThisYear,
        });

      const potAtYearStart = currentTotal;
      const yearNominalGrowth = nominalGrowthDecimalForAge(age);
      const growthThisYear = currentTotal * yearNominalGrowth;
      const potAfterGrowth = currentTotal + growthThisYear;
      if (age <= endAge) cumulativeGrowthDuringRetirement += growthThisYear;
      const actualWithdrawalCapped = Math.min(solved.grossPotWithdrawal, potAfterGrowth);
      currentTotal = potAfterGrowth - actualWithdrawalCapped;
      if (currentTotal < 0) currentTotal = 0;

      const finances = computeFinancesForPotWithdrawal({
        potWithdrawal: actualWithdrawalCapped,
        ...statePensionParams,
        ...dbPensionParams,
        annuityAnnual: annuityThisYear,
        age,
        statePensionAge,
        includeStatePensionInWithdrawal,
        taxFreeOption,
        cumulativeTaxFreeWithdrawn,
        taxFreeLumpSumCap,
        calculateTax,
        lumpSumThisYear,
        cashBufferAnnual: cashBufferThisYear,
      });
      cumulativeTaxFreeWithdrawn = finances.updatedCumulativeTaxFreeWithdrawn;
      if (age <= endAge && Math.round(finances.taxPaid) === 0) zeroTaxYearsAchieved += 1;

      const inflationFactor = inflationFactorAtYearsFromNow(yearsFromNow);
      const chartPotNominal = age === retirementAge ? potAtRetirementBeforeLumpSum : potAtYearStart;
      const totalReal = chartPotNominal / inflationFactor;
      const potDeductionAnnuityThisYear = age === retirementAge ? annuityPurchase : 0;
      const userStatePensionThisYear = userStatePensionAtAge(age);
      const partnerStatePensionThisYear = partnerStatePensionAtAge(age);
      const statePensionThisYear = userStatePensionThisYear + partnerStatePensionThisYear;
      const totalGrossIncome = actualWithdrawalCapped + statePensionThisYear + dbPensionThisYear + annuityThisYear;

      const chartBufferBalance = cashBufferEnabled && age >= spreadStart && age <= spreadEnd
        ? cashBufferBalanceAfterYear()
        : null;

      if (age === endAge) {
        cashBufferBalanceAtPlanAge = cashBufferEnabled ? Math.round(Math.max(0, cashBufferBalance)) : 0;
      }

      data.push({
        age, phase: age > endAge ? 'buffer' : 'withdrawal',
        totalNominal: Math.round(chartPotNominal),
        totalReal: Math.round(totalReal),
        potEndNominal: Math.round(currentTotal),
        potGrowth: Math.round(growthThisYear),
        potContribution: 0,
        potDeductionLumpSum: Math.round(lumpSumThisYear),
        potDeductionAnnuity: Math.round(potDeductionAnnuityThisYear),
        potDeductionWithdrawal: Math.round(actualWithdrawalCapped),
        withdrawal: Math.round(actualWithdrawalCapped),
        statePension: Math.round(statePensionThisYear),
        userStatePension: Math.round(userStatePensionThisYear),
        partnerStatePension: Math.round(partnerStatePensionThisYear),
        dbPension: Math.round(dbPensionThisYear),
        annuity: Math.round(annuityThisYear),
        cashBuffer: Math.round(cashBufferThisYear),
        cashBufferBalance: chartBufferBalance,
        totalIncome: Math.round(totalGrossIncome),
        taxPaid: Math.round(finances.taxPaid),
        netIncome: Math.round(finances.displayNet),
        taxFreeLumpSum: Math.round(lumpSumThisYear),
        taxFreeAmount: Math.round(finances.taxFreeAmount),
        taxableIncome: Math.round(finances.taxableIncome),
        targetNet: Math.round(targetNet),
        customTarget,
      });
    }

    return {
      data,
      potAtRetirementBeforeWithdrawals,
      potAtRetirementBeforeLumpSum,
      taxFreeLumpSumAtRetirement: taxFreeLumpSum,
      cumulativeGrowthDuringRetirement,
      annuityPurchase,
      annuityAnnualGross,
      cashBufferBalanceAtPlanAge,
      zeroTaxYearsAchieved,
    };
  };

  const {
    data,
    potAtRetirementBeforeWithdrawals,
    potAtRetirementBeforeLumpSum,
    taxFreeLumpSumAtRetirement,
    cumulativeGrowthDuringRetirement,
    annuityPurchase,
    annuityAnnualGross,
    cashBufferBalanceAtPlanAge,
    zeroTaxYearsAchieved,
  } = generateData();

  const annuityPurchasePercentDisplay = annuityPurchasePercentFromAmount(
    annuityPurchase,
    potAtRetirementBeforeWithdrawals || 0,
  );
  const annuityPurchaseSliderPercent = annuityPurchaseAmountExact != null
    ? annuityPurchasePercentDisplay
    : annuityPurchasePercent;

  const annuityIncomeRateDisplay = annuityIncomeRatePercentFromGross(
    annuityAnnualGross,
    annuityPurchase,
  );
  const annuityIncomeSliderRate = annuityAnnualGrossExact != null
    ? annuityIncomeRateDisplay
    : annuityIncomeRatePercent;
  const annuityIncomeMinGross = annuityPurchase * (ANNUITY_INCOME_RATE_MIN / 100);
  const annuityIncomeMaxGross = annuityPurchase * (ANNUITY_INCOME_RATE_MAX / 100);

  React.useEffect(() => {
    const spreadEndForFeasibility = (() => {
      if (cashBufferMode === CASH_BUFFER_MODE_BRIDGE) {
        return Math.min(lifeExpectancy, Math.max(currentAge, retirementAge - 1));
      }
      if (cashBufferMode === CASH_BUFFER_MODE_TAX) {
        return lifeExpectancy;
      }
      return cashBufferSpreadEndAge ?? Math.min(
        retirementAge + CASH_BUFFER_DEFAULT_SPREAD_YEARS - 1,
        lifeExpectancy,
      );
    })();
    const getBufferTargetForFeasibility = (age) => getTargetNetForAge(age);
    const maxPreRet = cashBufferEnabled && cashBufferAmount > 0
      ? maxPreRetirementBridgeYears({
        initialBalance: cashBufferAmount,
        retirementAge,
        currentAge,
        spreadEndAge: spreadEndForFeasibility,
        getTargetNetForAge: getBufferTargetForFeasibility,
        nominalGrowthRate: CASH_BUFFER_NOMINAL_GROWTH_RATE,
        maxLookback: cashBufferPreRetirementLookbackCap(currentAge, retirementAge),
      })
      : 0;
    const minStart = Math.max(currentAge + 1, retirementAge - maxPreRet);
    const max = lifeExpectancy;
    let start = cashBufferSpreadStartAge ?? retirementAge;
    let end = cashBufferSpreadEndAge ?? Math.min(retirementAge + CASH_BUFFER_DEFAULT_SPREAD_YEARS - 1, max);
    if (cashBufferMode === CASH_BUFFER_MODE_BRIDGE) {
      start = minStart;
      end = Math.max(minStart, retirementAge - 1);
    } else {
      start = Math.max(minStart, Math.min(max, start));
      end = Math.max(currentAge, Math.min(max, end));
    }
    if (end < start) end = start;
    if (cashBufferSpreadStartAge !== start) setCashBufferSpreadStartAge(start);
    if (cashBufferSpreadEndAge !== end) setCashBufferSpreadEndAge(end);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- clamp spread when plan inputs change
  }, [
    retirementAge,
    lifeExpectancy,
    currentAge,
    cashBufferEnabled,
    cashBufferMode,
    cashBufferAmount,
    withdrawalAmount,
    inflateWithdrawals,
    inflationRate,
    useSimulatedInflation,
    inflationSimSeed,
    cashBufferSpreadEndAge,
    cashBufferMode,
  ]);

  // ───── Safe withdrawal simulation (hooks must run before any early return) ─
  const calculateSimulatedSafeRate = () => {
    if (!potAtRetirementBeforeWithdrawals) return { safeWithdrawal: 0, safeRate: 0, safeGrossWithdrawal: 0 };

    const potAtRetirement = potAtRetirementBeforeWithdrawals;
    const targetAge = lifeExpectancy + 2;
    const targetYears = targetAge - retirementAge;

    const simulateRetirement = (baseNetTarget) => {
      let currentPot = Math.max(0, potAtRetirement - annuityPurchase);
      let cumulativeTaxFreeWithdrawnSim = 0;
      const simSpreadStart = cashBufferSpreadStartEffective;
      const simSpreadEnd = cashBufferSpreadEndEffective;
      const simGetTargetNetForAge = (age) => resolveTargetNetAtAge({
        age,
        baseTarget: baseNetTarget,
        inflateWithdrawals,
        retirementAge,
        currentAge,
        overrides: effectiveIncomeOverrides,
        pathMap: inflationPathMap,
        flatInflationPct: inflationRate,
        baseCalendarYear,
      });
      let simCashBufferBalance = cashBufferEnabled ? cashBufferAmount : 0;
      if (cashBufferEnabled && simSpreadStart < retirementAge) {
        simCashBufferBalance = cashBufferBalanceAtAge({
          initialBalance: cashBufferAmount,
          spreadStartAge: simSpreadStart,
          targetAge: retirementAge,
          retirementAge,
          spreadEndAge: simSpreadEnd,
          getTargetNetForAge: simGetTargetNetForAge,
          nominalGrowthRate: CASH_BUFFER_NOMINAL_GROWTH_RATE,
        });
      }
      for (let year = 0; year < targetYears; year++) {
        const age = retirementAge + year;
        const annuityThisYear = annuityGrossForAge({
          age,
          retirementAge,
          lifeExpectancy,
          annuityAnnualGross,
        });
        const targetNet = simGetTargetNetForAge(age);

        let simCashBufferThisYear = 0;
        if (
          cashBufferEnabled
          && age >= simSpreadStart
          && age <= simSpreadEnd
          && simCashBufferBalance > 0
        ) {
          const projected = computeCashBufferDrawForYear({
            balance: simCashBufferBalance,
            nominalGrowthRate: CASH_BUFFER_NOMINAL_GROWTH_RATE,
            targetNet,
            age,
            retirementAge,
            spreadEndAge: simSpreadEnd,
          });
          simCashBufferThisYear = projected.draw;
          simCashBufferBalance = projected.balanceAfter;
        }

        const solved = solvePotWithdrawalForNetTarget({
          targetNet,
          age,
          statePensionAge,
          ...statePensionParams,
          ...dbPensionParams,
          annuityAnnual: annuityThisYear,
          includeStatePensionInWithdrawal,
          taxFreeOption,
          cumulativeTaxFreeWithdrawn: cumulativeTaxFreeWithdrawnSim,
          taxFreeLumpSumCap,
          calculateTax,
          cashBufferAnnual: simCashBufferThisYear,
        });

        const yearNominalGrowth = nominalGrowthDecimalForAge(age);
        const growthThisYear = currentPot * yearNominalGrowth;
        const potAfterGrowth = currentPot + growthThisYear;
        const actualWithdrawalCapped = Math.min(solved.grossPotWithdrawal, potAfterGrowth);

        if (taxFreeOption === 'phased') {
          const finances = computeFinancesForPotWithdrawal({
            potWithdrawal: actualWithdrawalCapped,
            ...statePensionParams,
            ...dbPensionParams,
            annuityAnnual: annuityThisYear,
            age,
            statePensionAge,
            taxFreeOption,
            cumulativeTaxFreeWithdrawn: cumulativeTaxFreeWithdrawnSim,
            taxFreeLumpSumCap,
            calculateTax,
          });
          cumulativeTaxFreeWithdrawnSim = finances.updatedCumulativeTaxFreeWithdrawn;
        }

        currentPot = potAfterGrowth - actualWithdrawalCapped;
        if (currentPot <= 0) {
          const yearsShort = targetYears - year - 1;
          return -(yearsShort * 100000);
        }
      }
      return currentPot;
    };

    let lowWithdrawal = 0;
    let highWithdrawal = potAtRetirement / 10;
    let bestWithdrawal = lowWithdrawal;
    while (simulateRetirement(highWithdrawal) > 0 && highWithdrawal < potAtRetirement) highWithdrawal *= 2;
    for (let iteration = 0; iteration < 30; iteration++) {
      const testWithdrawal = (lowWithdrawal + highWithdrawal) / 2;
      const remainingPot = simulateRetirement(testWithdrawal);
      if (Math.abs(remainingPot) < 100) { bestWithdrawal = testWithdrawal; break; }
      if (remainingPot > 0) { lowWithdrawal = testWithdrawal; bestWithdrawal = testWithdrawal; }
      else highWithdrawal = testWithdrawal;
    }

    const safeWithdrawal = Math.round(bestWithdrawal / 500) * 500;
    const year1Annuity = annuityGrossForAge({
      age: retirementAge,
      retirementAge,
      lifeExpectancy,
      annuityAnnualGross,
    });
    const year1TargetNet = resolveTargetNetAtAge({
      age: retirementAge,
      baseTarget: safeWithdrawal,
      inflateWithdrawals,
      retirementAge,
      currentAge,
      overrides: effectiveIncomeOverrides,
      pathMap: inflationPathMap,
      flatInflationPct: inflationRate,
      baseCalendarYear,
    });
    const year1Solved = solvePotWithdrawalForNetTarget({
      targetNet: year1TargetNet,
      age: retirementAge,
      statePensionAge,
      ...statePensionParams,
      ...dbPensionParams,
      annuityAnnual: year1Annuity,
      includeStatePensionInWithdrawal,
      taxFreeOption,
      cumulativeTaxFreeWithdrawn: 0,
      taxFreeLumpSumCap,
      calculateTax,
    });
    const safeGrossWithdrawal = year1Solved.grossPotWithdrawal;
    const safeRate = (safeGrossWithdrawal / potAtRetirement) * 100;
    return { safeWithdrawal, safeRate, safeGrossWithdrawal };
  };

  const { safeWithdrawal: simulatedSafeWithdrawal, safeRate: simulatedSafeRate } = data.length > 0
    ? calculateSimulatedSafeRate()
    : { safeWithdrawal: 0, safeRate: 0, safeGrossWithdrawal: 0 };

  React.useLayoutEffect(() => {
    if (data.length === 0 || withdrawalSeededRef.current || simulatedSafeWithdrawal <= 0) return;
    setWithdrawalAmount(simulatedSafeWithdrawal);
    withdrawalSeededRef.current = true;
  }, [data.length, simulatedSafeWithdrawal]);

  if (!data || data.length === 0) {
    return (
      <div className="w-full h-full p-6 bg-slate-50 flex items-center justify-center">
        <p className="text-rose-600">Error generating retirement data. Please adjust your parameters.</p>
      </div>
    );
  }

  const retirementData = data.find(d => d.age === retirementAge);
  const withdrawalData = data.filter(d => d.phase === 'withdrawal');

  const year1GrossWithdrawal = retirementData?.withdrawal || 0;
  const year1TaxableIncome = retirementData?.taxableIncome || 0;

  const withdrawalPercentage = potAtRetirementBeforeWithdrawals > 0
    ? (year1GrossWithdrawal / potAtRetirementBeforeWithdrawals * 100).toFixed(2)
    : '0.00';

  const personalAllowanceSliderPct = valueToSliderPercent(WITHDRAWAL_LAYOUT, personalAllowance);
  const basicRateThresholdSliderPct = valueToSliderPercent(WITHDRAWAL_LAYOUT, basicRateThreshold);

  const chartData = data.filter((d) => d.age <= lifeExpectancy);
  const cashBufferMarkerAge = cashBufferEnabled && cashBufferAmount > 0
    ? cashBufferDepletedAgeFromChartData(chartData, {
      spreadStart: cashBufferSpreadStartModeEffective,
      spreadEnd: cashBufferSpreadEndModeEffective,
    })
    : null;

  const renderChartToolbar = (isFullscreen) => (
    <div className="flex items-center gap-2 flex-wrap justify-end">
      <button
        type="button"
        onClick={() => setShowIncomeLines(!showIncomeLines)}
        className={`text-[11px] font-medium px-2 py-1 rounded-full border transition ${
          showIncomeLines
            ? 'bg-slate-900 text-white border-slate-900'
            : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
        }`}
      >
        {showIncomeLines ? '✓ Income lines' : '+ Income lines'}
      </button>
      <button
        type="button"
        onClick={handleShare}
        title="Creates a link with your pension inputs and assumptions encoded in the URL"
        className="text-[11px] font-medium px-2 py-1 rounded-full border border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:text-slate-900 transition flex items-center gap-1"
      >
        <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path d="M9 4l-5 4 5 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" transform="rotate(180 8 8)" />
          <path d="M4 4h6a3 3 0 0 1 3 3v0a3 3 0 0 1-3 3H4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        Share results
      </button>
      <button
        type="button"
        onClick={() => setChartFullscreenOpen(!isFullscreen)}
        title={isFullscreen ? 'Exit full screen' : 'Expand chart to full screen'}
        aria-label={isFullscreen ? 'Exit full screen' : 'Expand chart to full screen'}
        className="text-[11px] font-medium p-1.5 rounded-full border border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:text-slate-900 transition flex items-center justify-center"
      >
        {isFullscreen ? (
          <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M5 2H2v3M2 2l4 4M11 2h3v3M14 2l-4 4M5 14H2v-3M2 14l4-4M11 14h3v-3M14 14l-4-4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ) : (
          <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M2 6V2h4M2 2l4 4M14 6V2h-4M14 2l-4 4M2 10v4h4M2 14l4-4M14 10v4h-4M14 14l-4-4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </button>
    </div>
  );

  const renderProjectionChart = (height) => {
    const isNum = typeof height === 'number';
    const incomeActive = showIncomeLines;
    const legend = [
      { label: 'Pot (nominal)', color: 'oklch(0.62 0.13 162)' },
      { label: "Today's money", color: 'oklch(0.44 0.09 165)' },
      ...(incomeActive ? [{ label: 'Drawdown', color: 'oklch(0.62 0.13 162)' }] : []),
      ...(incomeActive && cashBufferEnabled && cashBufferAmount > 0 ? [{ label: 'Cash buffer', color: 'oklch(0.64 0.12 215)' }] : []),
      ...(incomeActive && includeStatePensionInWithdrawal ? [{ label: 'State pension', color: 'oklch(0.62 0.13 250)' }] : []),
      ...(incomeActive && dbPensionEnabled ? [{ label: 'DB pension', color: 'oklch(0.58 0.11 195)' }] : []),
      ...(incomeActive && annuityEnabled ? [{ label: 'Annuity', color: 'oklch(0.60 0.13 290)' }] : []),
      ...(incomeActive && effectiveIncomeOverrides.length > 0 ? [{ label: 'Target net', color: 'oklch(0.55 0.10 162)', dashed: true }] : []),
    ];
    return (
      <div style={{ width: '100%', height: isNum ? height : '100%', display: 'flex', flexDirection: 'column' }}>
        <div style={{ flex: '1 1 auto', minHeight: 0 }}>
          <ProjectionChart
            data={chartData}
            currentAge={currentAge}
            retirementAge={retirementAge}
            statePensionAge={statePensionAge}
            partnerStatePensionAge={partnerStatePensionAge}
            includePartnerStatePension={includePartnerStatePension}
            lifeExpectancy={lifeExpectancy}
            cashBufferEnabled={cashBufferEnabled && cashBufferAmount > 0}
            cashBufferSpreadStartEffective={cashBufferEnabled && cashBufferAmount > 0 && cashBufferSpreadStartModeEffective < retirementAge ? cashBufferSpreadStartModeEffective : null}
            cashBufferMarkerAge={cashBufferMarkerAge}
            annuityEnabled={annuityEnabled}
            dbPensionEnabled={dbPensionEnabled}
            dbPensionAge={dbPensionAge}
            includeStatePension={includeStatePensionInWithdrawal}
            showIncome={showIncomeLines}
            incomeOverrides={effectiveIncomeOverrides}
          />
        </div>
        <div className="flex flex-wrap justify-center gap-x-4 gap-y-1.5 pt-3 text-[12px] text-slate-500">
          {legend.map((it) => (
            <span key={it.label} className="inline-flex items-center gap-1.5">
              <span style={{
                width: 14,
                height: 0,
                borderTop: `2px ${it.dashed ? 'dashed' : 'solid'} ${it.color}`,
                display: 'inline-block',
              }}
              />
              {it.label}
            </span>
          ))}
        </div>
      </div>
    );
  };

  // ───── Derived display values ─────────────────────────────────────────────
  const yearsLeft = retirementAge - currentAge;
  // Real value of the headline pot at retirement — must be invariant to target income.
  // In lump-sum mode the hero shows the remaining drawdown pot after the tax-free slice.
  const inflationFactorAtRetirement = inflationFactorAtYearsFromNow(yearsLeft);
  const heroPotAtRetirement = taxFreeOption === 'lumpsum'
    ? potAtRetirementBeforeWithdrawals
    : potAtRetirementBeforeLumpSum;
  const potAtRetirementReal = heroPotAtRetirement / inflationFactorAtRetirement;

  // Withdrawal rate tone
  const withdrawalRate = potAtRetirementBeforeWithdrawals > 0
    ? (year1GrossWithdrawal / potAtRetirementBeforeWithdrawals * 100) : 0;
  const safeThreshold = simulatedSafeRate + 0.2;
  const acceptableThreshold = simulatedSafeRate + 0.8;
  const riskyThreshold = simulatedSafeRate + 1.5;
  let wrTone = 'good', wrText = 'Safe';
  if (withdrawalRate >= riskyThreshold) { wrTone = 'bad'; wrText = 'Unsustainable'; }
  else if (withdrawalRate >= acceptableThreshold) { wrTone = 'warn'; wrText = 'Risky'; }
  else if (withdrawalRate >= safeThreshold) { wrTone = 'okay'; wrText = 'Acceptable'; }

  const year1PhasedGross = solvePotWithdrawalForNetTarget({
    targetNet: withdrawalAmount,
    age: retirementAge,
    statePensionAge,
    ...statePensionParams,
    ...dbPensionParams,
    annuityAnnual: annuityGrossForAge({
      age: retirementAge,
      retirementAge,
      lifeExpectancy,
      annuityAnnualGross,
    }),
    includeStatePensionInWithdrawal,
    taxFreeOption: 'phased',
    cumulativeTaxFreeWithdrawn: 0,
    taxFreeLumpSumCap,
    calculateTax,
  }).grossPotWithdrawal;

  // Funds last
  const fundsDepletedAge = data.find(
    (d) => d.phase === 'withdrawal' && d.age <= lifeExpectancy && (d.potEndNominal ?? 0) === 0,
  );
  const lastsToLifeExpectancy = !fundsDepletedAge;
  const depletedAge = fundsDepletedAge ? fundsDepletedAge.age : lifeExpectancy;
  const fundsTone = lastsToLifeExpectancy || depletedAge >= lifeExpectancy
    ? 'good' : (depletedAge >= lifeExpectancy - 5 ? 'warn' : 'bad');

  // Lifetime tax
  const totalTax = withdrawalData.reduce((s, d) => s + (d.taxPaid || 0), 0);
  const planWithdrawalYears = withdrawalData.filter((d) => d.age <= lifeExpectancy);
  const totalWithdrawnFromDrawdown = withdrawalData.reduce((s, d) => s + (d.withdrawal || 0), 0);
  const lumpSumTaken = planWithdrawalYears.reduce((s, d) => s + (d.taxFreeLumpSum || 0), 0);
  const totalWithdrawn = totalWithdrawnFromDrawdown + lumpSumTaken;
  const totalStatePensionPaid = withdrawalData.reduce((s, d) => s + (d.statePension || 0), 0);
  const totalAnnuityPaid = withdrawalData.reduce((s, d) => s + (d.annuity || 0), 0);
  const totalIncome = totalWithdrawn + totalStatePensionPaid + totalAnnuityPaid;
  const effectiveTaxRate = totalIncome > 0 ? (totalTax / totalIncome) * 100 : 0;
  let taxTone = 'neutral', taxText = '—';
  if (effectiveTaxRate < 12) { taxTone = 'good'; taxText = 'Excellent'; }
  else if (effectiveTaxRate < 18) { taxTone = 'good'; taxText = 'Good'; }
  else if (effectiveTaxRate < 25) { taxTone = 'okay'; taxText = 'Acceptable'; }
  else { taxTone = 'warn'; taxText = 'High'; }

  // Tax-free allowance (plan through life expectancy)
  const totalTaxFreeFromWithdrawals = planWithdrawalYears.reduce((s, d) => s + (d.taxFreeAmount || 0), 0);
  const taxFreeUsedInPlan = totalTaxFreeFromWithdrawals + lumpSumTaken;
  const potTaxFreeEntitlement = Math.min(potAtRetirementBeforeLumpSum * 0.25, taxFreeLumpSumCap);
  const taxFreeLifetimeCap = taxFreeLumpSumCap;
  const taxFreeUnusedFromPot = Math.max(0, potTaxFreeEntitlement - taxFreeUsedInPlan);
  const taxFreeUsagePct = potTaxFreeEntitlement > 0 ? (taxFreeUsedInPlan / potTaxFreeEntitlement) * 100 : 0;
  const taxFreeCapBindsPot = potAtRetirementBeforeLumpSum * 0.25 >= taxFreeLumpSumCap - 1;

  let taxFreeExhaustionAge = null;
  if (taxFreeOption === 'phased') {
    let cumulativeTaxFree = 0;
    for (const year of planWithdrawalYears) {
      cumulativeTaxFree += year.taxFreeAmount || 0;
      if (cumulativeTaxFree >= potTaxFreeEntitlement - 500) {
        taxFreeExhaustionAge = year.age;
        break;
      }
    }
  }

  // Breakdown (through life expectancy)
  const breakdownYears = planWithdrawalYears;
  const lifetimeNetReceived = breakdownYears.reduce((s, d) => s + (d.netIncome || 0), 0);
  const lifetimeGrossPot = breakdownYears.reduce((s, d) => s + (d.withdrawal || 0), 0) + lumpSumTaken;
  const lifetimeGrossUserStatePension = breakdownYears.reduce((s, d) => s + (d.userStatePension || 0), 0);
  const lifetimeGrossPartnerStatePension = breakdownYears.reduce((s, d) => s + (d.partnerStatePension || 0), 0);
  const lifetimeGrossStatePension = lifetimeGrossUserStatePension + lifetimeGrossPartnerStatePension;
  const lifetimeGrossAnnuity = breakdownYears.reduce((s, d) => s + (d.annuity || 0), 0);
  const lifetimeGrossDbPension = breakdownYears.reduce((s, d) => s + (d.dbPension || 0), 0);
  const lifetimeCashBuffer = chartData.reduce((s, d) => s + (d.cashBuffer || 0), 0);
  const lifetimeGrossTotal = lifetimeGrossPot + lifetimeGrossStatePension + lifetimeGrossDbPension
    + lifetimeGrossAnnuity + lifetimeCashBuffer;
  const potIncomeSharePct = lifetimeGrossTotal > 0 ? (lifetimeGrossPot / lifetimeGrossTotal) * 100 : 100;
  const annuityIncomeSharePct = lifetimeGrossTotal > 0 ? (lifetimeGrossAnnuity / lifetimeGrossTotal) * 100 : 0;
  const dbIncomeSharePct = lifetimeGrossTotal > 0 ? (lifetimeGrossDbPension / lifetimeGrossTotal) * 100 : 0;
  const cashBufferIncomeSharePct = lifetimeGrossTotal > 0 ? (lifetimeCashBuffer / lifetimeGrossTotal) * 100 : 0;
  const stateIncomeSharePct = lifetimeGrossTotal > 0 ? (lifetimeGrossStatePension / lifetimeGrossTotal) * 100 : 0;
  const userStateIncomeSharePct = lifetimeGrossTotal > 0
    ? (lifetimeGrossUserStatePension / lifetimeGrossTotal) * 100
    : 0;
  const partnerStateIncomeSharePct = lifetimeGrossTotal > 0
    ? (lifetimeGrossPartnerStatePension / lifetimeGrossTotal) * 100
    : 0;

  // ───── JSX ────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen w-full bg-stone-50 text-slate-900" style={{ fontFamily: 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif' }}>

      {/* Disclaimer banner */}
      <div className="w-full border-b border-amber-200 bg-amber-50/70">
        <div className="max-w-[1280px] mx-auto px-10 py-2 flex items-center justify-center gap-2.5 text-[12px] text-amber-900">
          <svg width="13" height="13" viewBox="0 0 16 16" fill="none" className="flex-shrink-0">
            <path d="M8 1L1 14h14L8 1z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/>
            <path d="M8 6v4M8 11.5v.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
          </svg>
          <span><span className="font-medium">Educational tool only</span> — not financial advice.</span>
          <button
            type="button"
            onClick={openDisclaimer}
            className="underline underline-offset-2 decoration-amber-700/40 hover:decoration-amber-900 transition font-medium"
          >
            Full disclaimer
          </button>
        </div>
      </div>

      <div className="max-w-[1280px] mx-auto px-10 py-8">

        {/* Topbar */}
        <header className="flex flex-wrap items-center justify-between gap-y-3 pb-7 mb-7 border-b border-slate-200">
          <Link to="/" className="flex items-center gap-3 no-underline group">
            <svg width="36" height="36" viewBox="0 0 36 36" fill="none" aria-hidden="true">
              <path d="M18 4 C 11 4, 7 13, 7 21 C 7 27, 12 32, 18 32 C 24 32, 29 27, 29 21 C 29 13, 25 4, 18 4 Z"
                    stroke="#059669" strokeWidth="1.8" fill="none"/>
              <circle cx="18" cy="21" r="5" fill="#059669"/>
            </svg>
            <div>
              <div className="text-[26px] leading-none tracking-tight text-slate-900 group-hover:text-slate-700 transition" style={{ fontFamily: 'ui-serif, Georgia, "Times New Roman", serif' }}>
                caniretire<span className="text-slate-400">.uk</span>
              </div>
              <div className="mt-1.5 text-[11px] font-medium tracking-wide text-emerald-700">
                Money-in-your-pocket pension planning
              </div>
            </div>
          </Link>
          <div className="flex flex-wrap items-center gap-4">
            <nav className="flex items-center gap-5 text-[13px] text-slate-600" aria-label="Primary">
              <a href="/" className="hover:text-slate-900 transition">Calculator</a>
              <a href="/articles/" className="hover:text-slate-900 transition">Articles</a>
              <a href="/about/" className="hover:text-slate-900 transition">About</a>
            </nav>
            <button
              type="button"
              onClick={() => setAssumptionsPanelOpen(true)}
              className="px-3.5 py-1.5 rounded-full border border-slate-200 bg-white text-[13px] text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition flex items-center gap-1.5"
            >
              <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="2.5" stroke="currentColor" strokeWidth="1.4"/><path d="M8 1.5v2M8 12.5v2M1.5 8h2M12.5 8h2M3.5 3.5l1.4 1.4M11.1 11.1l1.4 1.4M3.5 12.5l1.4-1.4M11.1 4.9l1.4-1.4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>
              Assumptions
            </button>
          </div>
        </header>

        {/* Intro */}
        <section className="mb-11">
          <h1 className="text-[36px] sm:text-[54px] leading-[1.02] tracking-tight text-slate-900 font-normal mb-4" style={{ fontFamily: 'ui-serif, Georgia, "Times New Roman", serif', textWrap: 'balance' }}>
            Will your pension <em className="italic text-emerald-700">actually</em> last?
          </h1>
          <p className="text-[16px] leading-[1.55] text-slate-600 mb-6" style={{ textWrap: 'pretty' }}>
            You have a pot figure in mind and a retirement age on the calendar. The harder question is what actually lands in your bank each year, and whether it lasts.{' '}
            Most UK calculators stop the moment you retire, or quote <strong className="font-semibold text-slate-900">gross</strong> numbers you cannot spend.{' '}
            <strong className="font-semibold text-slate-900">caniretire.uk</strong> lets you plan in{' '}
            <strong className="font-semibold text-slate-900">net income</strong>: the take-home pay you want. We work out the draw from your pot, the tax, and when state pension chips in.{' '}
            Then we keep modelling through drawdown, lump-sum vs phased tax-free cash, and inflation in <em>today&apos;s money</em>, year by year.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-7 mt-1">
            <div className="flex flex-col pt-3.5 border-t-[1.5px] border-emerald-600">
              <div className="font-mono text-[10.5px] uppercase tracking-wider text-emerald-700 font-medium mb-2.5">Drawdown</div>
              <div className="text-[15px] font-semibold text-slate-900 tracking-tight leading-snug mb-1.5" style={{ textWrap: 'balance' }}>Growth through retirement</div>
              <div className="text-[13px] text-slate-600 leading-relaxed" style={{ textWrap: 'pretty' }}>Your pot does not freeze when you stop work. We model it growing or shrinking while you draw down.</div>
            </div>
            <div className="flex flex-col pt-3.5 border-t-[1.5px] border-emerald-600">
              <div className="font-mono text-[10.5px] uppercase tracking-wider text-emerald-700 font-medium mb-2.5">Tax strategy</div>
              <div className="text-[15px] font-semibold text-slate-900 tracking-tight leading-snug mb-1.5" style={{ textWrap: 'balance' }}>Lump-sum vs phased</div>
              <div className="text-[13px] text-slate-600 leading-relaxed" style={{ textWrap: 'pretty' }}>The 25% tax-free choice shapes your lifetime tax bill. Compare both paths side by side.</div>
            </div>
            <div className="flex flex-col pt-3.5 border-t-[1.5px] border-emerald-600">
              <div className="font-mono text-[10.5px] uppercase tracking-wider text-emerald-700 font-medium mb-2.5">State pension</div>
              <div className="text-[15px] font-semibold text-slate-900 tracking-tight leading-snug mb-1.5" style={{ textWrap: 'balance' }}>Folded in at the right age</div>
              <div className="text-[13px] text-slate-600 leading-relaxed" style={{ textWrap: 'pretty' }}>Starts mid-drawdown and cuts what you need from your pot, at the amount you expect.</div>
            </div>
            <div className="flex flex-col pt-3.5 border-t-[1.5px] border-emerald-600">
              <div className="font-mono text-[10.5px] uppercase tracking-wider text-emerald-700 font-medium mb-2.5">Real value</div>
              <div className="text-[15px] font-semibold text-slate-900 tracking-tight leading-snug mb-1.5" style={{ textWrap: 'balance' }}>Shown in today&apos;s money</div>
              <div className="text-[13px] text-slate-600 leading-relaxed" style={{ textWrap: 'pretty' }}>£500k in 25 years is not £500k now. Every projection shows the inflation-adjusted figure too.</div>
            </div>
          </div>
        </section>

        {/* ─── Main grid ──────────────────────────────────────────────────── */}
        <div className="grid gap-9 grid-cols-1 lg:grid-cols-[380px_1fr]">

          {/* ─── INPUTS ──────────────────────────────────────────────────── */}
          <section>
            <h2 className="text-[11px] font-semibold tracking-wider uppercase text-slate-500 mb-4">Your plan</h2>

            {/* Age range */}
            <div className="py-4 first:pt-1">
              <div className="flex justify-between gap-6 mb-2">
                <div>
                  <div className="text-xs text-slate-500">Today, you're</div>
                  <div className="text-[26px] font-medium tracking-tight tabular-nums leading-none mt-1">{currentAge}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-slate-500">You'll retire at</div>
                  <div className="text-[26px] font-medium tracking-tight tabular-nums leading-none mt-1">{retirementAge}</div>
                </div>
              </div>
              <div className="relative h-7 mb-1">
                <div className="absolute w-full top-1/2 -translate-y-1/2 h-[3px] bg-slate-200 rounded-full pointer-events-none"></div>
                <div
                  className="absolute top-1/2 -translate-y-1/2 h-[3px] bg-slate-900 rounded-full pointer-events-none"
                  style={{
                    left: `${((currentAge - 16) / 64) * 100}%`,
                    width: `${((retirementAge - currentAge) / 64) * 100}%`
                  }}
                />
                <input
                  type="range" min="16" max="80" step="1" value={currentAge}
                  onChange={(e) => {
                    const v = parseInt(e.target.value);
                    if (v < retirementAge) setCurrentAge(v);
                  }}
                  className="dual-range-slider"
                  style={{ position: 'absolute', width: '100%', top: 0, height: '1.75rem', zIndex: 4 }}
                />
                <input
                  type="range" min="16" max="80" step="1" value={retirementAge}
                  onChange={(e) => {
                    const v = parseInt(e.target.value, 10);
                    const clamped = Math.max(minRetirementAge, Math.min(80, v));
                    if (clamped > currentAge) setRetirementAge(clamped);
                  }}
                  className="dual-range-slider"
                  style={{ position: 'absolute', width: '100%', top: 0, height: '1.75rem', zIndex: 5 }}
                />
              </div>
              <div className="flex justify-between text-[11px] text-slate-400 tabular-nums">
                <span>16</span><span>48</span><span>80</span>
              </div>
              {retirementAge === potAccessAge && !dismissPotAccessRetirementNotice && (
                <div
                  className="mt-2 relative text-[11.5px] leading-snug text-sky-900 bg-sky-50 border border-sky-200 rounded-lg pl-2.5 pr-8 py-2"
                  role="status"
                >
                  <button
                    type="button"
                    onClick={() => setDismissPotAccessRetirementNotice(true)}
                    className="absolute top-1.5 right-1.5 w-6 h-6 rounded-md text-sky-600 hover:text-sky-900 hover:bg-sky-100/80 flex items-center justify-center transition"
                    aria-label="Dismiss notice"
                  >
                    <svg width="10" height="10" viewBox="0 0 12 12" aria-hidden="true">
                      <path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                  </button>
                  <p>
                    Retirement is at the minimum pension pot access age ({potAccessAge}). The calculator does not model pension withdrawals before this age — use Cash buffer if you need income earlier. You can change this minimum in{' '}
                    <button
                      type="button"
                      onClick={() => setAssumptionsPanelOpen(true)}
                      className="font-medium text-sky-800 underline underline-offset-2 hover:text-sky-950"
                    >
                      Assumptions
                    </button>
                    .
                  </p>
                </div>
              )}
              <div className="mt-2 text-xs text-slate-500">
                {yearsLeft} accumulation years left
              </div>
            </div>

            {/* Current pot */}
            <FieldRow label="Current pension pot" value={`£${currentPension.toLocaleString()}`}
              editable rawValue={currentPension} onCommit={setCurrentPension} min={0} max={3000000}>
              <input
                type="range" min={0} max={POT_LAYOUT.total} step={1}
                value={valueToIndex(POT_LAYOUT, currentPension)}
                onChange={(e) => setCurrentPension(indexToValue(POT_LAYOUT, parseInt(e.target.value)))}
                className="rc-slider"
              />
              <SliderTicks ticks={['£0', '£100k', '£250k', '£1m', '£3m']} />
            </FieldRow>

            {/* Annual contribution */}
            <FieldRow
              label="Adding each year"
              labelHint="Total amount credited to your pension pot each year — your payments, employer contributions, and tax relief combined. Enter that full figure; we do not add employer or tax relief on top."
              value={`£${pensionContribution.toLocaleString()}/yr`}
              editable rawValue={pensionContribution} onCommit={setPensionContribution} min={0} max={200000} suffix="/yr"
              help={pensionContribution > 0 ? `£${(pensionContribution * yearsLeft).toLocaleString()} over ${yearsLeft} years` : null}
            >
              <input
                type="range" min={0} max={CONTRIB_LAYOUT.total} step={1}
                value={valueToIndex(CONTRIB_LAYOUT, pensionContribution)}
                onChange={(e) => setPensionContribution(indexToValue(CONTRIB_LAYOUT, parseInt(e.target.value)))}
                className="rc-slider"
              />
              <SliderTicks ticks={['£0', '£5k', '£25k', '£100k']} />
            </FieldRow>

            {/* Target net income + safe-rate (unified control) */}
            <FieldRow
              label="Target net income"
              labelHint="The take-home pay you want each year — money in your pocket after tax. We work out how much to draw from your pot, and factor in tax and state pension (when included) so you land on this net figure."
              value={`£${withdrawalAmount.toLocaleString()}/yr`}
              editable
              rawValue={withdrawalAmount}
              onCommit={(amount) => {
                withdrawalSeededRef.current = true;
                setWithdrawalAmount(amount);
              }}
              min={0}
              max={WITHDRAWAL_SLIDER_MAX}
              suffix="/yr"
            >
              <input
                type="range" min={0} max={WITHDRAWAL_LAYOUT.total} step={1}
                value={valueToIndex(WITHDRAWAL_LAYOUT, withdrawalAmount)}
                onChange={(e) => {
                  withdrawalSeededRef.current = true;
                  setWithdrawalAmount(indexToValue(WITHDRAWAL_LAYOUT, parseInt(e.target.value, 10)));
                }}
                className="rc-slider rc-slider-tax"
                style={{
                  '--pa-pct': `${personalAllowanceSliderPct}%`,
                  '--br-pct': `${basicRateThresholdSliderPct}%`,
                }}
              />

              {/* Threshold-anchored tick labels */}
              <div className="relative h-9 mt-2 text-[10.5px] tabular-nums">
                <span className="absolute left-0 top-0 text-slate-400">£0</span>
                <span className="absolute top-0 flex flex-col items-center" style={{ left: `${personalAllowanceSliderPct}%`, transform: 'translateX(-50%)' }}>
                  <span className="block w-px h-1.5 bg-emerald-500/60"></span>
                  <span className="text-emerald-700 font-medium mt-0.5">£{(personalAllowance/1000).toFixed(1)}k</span>
                  <span className="text-[9px] text-emerald-600/70 leading-tight">20% starts</span>
                </span>
                <span className="absolute top-0 flex flex-col items-center" style={{ left: `${basicRateThresholdSliderPct}%`, transform: 'translateX(-50%)' }}>
                  <span className="block w-px h-1.5 bg-amber-500/60"></span>
                  <span className="text-amber-700 font-medium mt-0.5">£{(basicRateThreshold/1000).toFixed(1)}k</span>
                  <span className="text-[9px] text-amber-600/70 leading-tight">40% starts</span>
                </span>
                <span className="absolute right-0 top-0 text-slate-400">£250k</span>
              </div>

              {/* Help line */}
              <div className="mt-2 text-xs text-slate-500 leading-relaxed">
                {withdrawalPercentage}% gross draw from pot at retirement
                {year1TaxableIncome > basicRateThreshold
                  ? ' · taxable income in 40% band'
                  : year1TaxableIncome > personalAllowance
                    ? ' · basic-rate tax on draw'
                    : ' · draw below personal allowance'}
              </div>

              {/* Inflation-adjust toggle */}
              <label className="mt-3 flex items-start gap-2.5 cursor-pointer group">
                <input
                  type="checkbox"
                  className="rc-checkbox"
                  checked={inflateWithdrawals}
                  onChange={(e) => setInflateWithdrawals(e.target.checked)}
                />
                <span className="text-[12.5px] text-slate-700 leading-snug">
                  <span className="font-medium inline-flex items-center flex-wrap gap-x-0">
                    Increase net income by inflation each year
                    <LabelHint placement="below" tooltipClassName="w-[min(300px,calc(100vw-2rem))]">
                      <span className="flex flex-col gap-2 text-[13px] leading-relaxed">
                        <span>
                          {inflateWithdrawals
                            ? 'Your target take-home income rises each year with your inflation assumption, so spending power stays level in real terms.'
                            : 'Your target stays at a fixed £/yr net figure. Nominal income does not rise, so purchasing power falls as prices increase.'}
                        </span>
                        <span className="tabular-nums text-slate-800 font-medium">
                          {inflateWithdrawals
                            ? (
                              <>
                                Year 5: £{Math.round(withdrawalAmount * withdrawalTargetMultiplierAtAge(retirementAge + 4)).toLocaleString()} net
                                {' · '}
                                Year 10: £{Math.round(withdrawalAmount * withdrawalTargetMultiplierAtAge(retirementAge + 9)).toLocaleString()} net
                              </>
                            )
                            : (
                              <>Fixed £{withdrawalAmount.toLocaleString()}/yr net at every year of retirement</>
                            )}
                        </span>
                        <span className="text-slate-500">
                          Uses {useSimulatedInflation
                            ? `simulated UK CPIH from Growth & inflation (${inflationSummaryLabel}).`
                            : `your inflation rate from Growth & inflation (${inflationRate}%/yr).`}
                        </span>
                      </span>
                    </LabelHint>
                  </span>
                </span>
              </label>

              {/* Safe-rate strip — integrated footer */}
              {simulatedSafeWithdrawal > 0 && (() => {
                const gap = withdrawalAmount - simulatedSafeWithdrawal;
                const isAtSafe = Math.abs(gap) < 1000;
                const isAbove = gap > 1000;

                const tone = isAtSafe
                  ? { wrap: 'border-emerald-200 bg-emerald-50/60', accent: 'text-emerald-700', bar: 'bg-emerald-500' }
                  : isAbove
                  ? { wrap: 'border-amber-200 bg-amber-50/60', accent: 'text-amber-800', bar: 'bg-amber-500' }
                  : { wrap: 'border-slate-200 bg-slate-50/60', accent: 'text-slate-600', bar: 'bg-slate-400' };

                const status = isAtSafe
                  ? 'You\'re drawing the maximum your pot can sustain.'
                  : isAbove
                  ? `£${Math.abs(gap).toLocaleString()}/yr above sustainable — funds may not last to ${lifeExpectancy}.`
                  : `Headroom of £${Math.abs(gap).toLocaleString()}/yr above your current target.`;

                return (
                  <div className={`mt-4 flex items-center gap-3 px-3.5 py-3 rounded-xl border ${tone.wrap}`}>
                    <span className={`w-0.5 self-stretch rounded-full ${tone.bar} flex-shrink-0`}></span>
                    <div className="flex-1 min-w-0">
                      <div className={`text-[10px] font-semibold tracking-wider uppercase ${tone.accent} mb-0.5`}>
                        {isAtSafe ? '✓ At sustainable net' : 'Sustainable net income'}
                      </div>
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="text-[18px] font-medium tracking-tight tabular-nums text-slate-900 leading-none">
                          £{simulatedSafeWithdrawal.toLocaleString()}
                        </span>
                        <span className="text-[12px] text-slate-500 tabular-nums leading-none">/yr net · {simulatedSafeRate.toFixed(1)}% gross draw</span>
                      </div>
                      <div className="mt-1 text-[11.5px] text-slate-600 leading-snug" style={{ textWrap: 'pretty' }}>{status}</div>
                    </div>
                    {!isAtSafe && (
                      <button
                        type="button"
                        onClick={() => {
                          withdrawalSeededRef.current = true;
                          setWithdrawalAmount(simulatedSafeWithdrawal);
                        }}
                        className="flex-shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-[12px] font-medium transition"
                      >
                        {isAbove ? 'Apply' : 'Max it'}
                        <svg width="11" height="11" viewBox="0 0 16 16" fill="none"><path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      </button>
                    )}
                  </div>
                );
              })()}
            </FieldRow>

            {/* ─── Advanced ────────────────────────────────────────────── */}
            <div className="mt-7">
              <h2 className="text-[11px] font-semibold tracking-wider uppercase text-slate-500 mb-1">Advanced</h2>

              <Disclosure
                title="Growth & inflation"
                summary={`${realGrowthRate >= 0 ? '+' : ''}${realGrowthRate.toFixed(1)}% real · ${inflationSummaryLabel}`}
              >
                <FieldRow label="Annual inflation" value={`${inflationRate.toFixed(1)}%`}>
                  <input
                    type="range" min="0" max="10" step="0.1"
                    value={inflationRate}
                    onChange={(e) => setInflationRate(Math.round(parseFloat(e.target.value) * 10) / 10)}
                    className="rc-slider"
                    disabled={useSimulatedInflation}
                  />
                  <SliderTicks ticks={['0%', '5%', '10%']} />
                </FieldRow>
                <label className="flex items-start gap-2.5 py-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="rc-checkbox"
                    checked={useSimulatedInflation}
                    onChange={(e) => setUseSimulatedInflation(e.target.checked)}
                  />
                  <span className="text-[13px] text-slate-700 leading-snug inline-flex items-start gap-1 flex-wrap">
                    <span>
                      Use simulated UK inflation (
                      <a
                        href={ONS_CPIH_ANNUAL_RATE_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-emerald-700 hover:text-emerald-800 underline underline-offset-2"
                        onClick={(e) => e.stopPropagation()}
                      >
                        CPIH model
                      </a>
                      )
                    </span>
                    <LabelHint
                      placement="below"
                      tooltipClassName="w-[min(300px,calc(100vw-2rem))]"
                    >
                      <span className="flex flex-col gap-2 text-[13px] leading-relaxed">
                        <span>
                          Projects year-by-year inflation using an AR(1) model calibrated to ONS CPIH annual rates (1989–2025), with bootstrapped shocks. Nominal investment growth varies each year (real return + that year&apos;s inflation). Re-run for a different synthetic path.
                        </span>
                        <span className="text-slate-500">
                          Calibrated to ONS CPIH annual rates, series {ONS_CPIH_SERIES_ID} (dataset {ONS_CPIH_DATASET_ID}), 1989–2025.
                        </span>
                      </span>
                    </LabelHint>
                  </span>
                </label>
                {useSimulatedInflation && inflationSim && (
                  <div className="mb-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-[12.5px] text-slate-600 leading-relaxed">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span>
                        Path avg {inflationSim.summary.mean}%/yr
                        {' · '}
                        range {inflationSim.summary.trough}–{inflationSim.summary.peak}%
                      </span>
                      <button
                        type="button"
                        onClick={() => setInflationSimSeed(Math.floor(Math.random() * 2 ** 32))}
                        className="shrink-0 text-[12px] font-medium text-emerald-700 hover:text-emerald-800"
                      >
                        Re-run simulation
                      </button>
                    </div>
                  </div>
                )}
                <FieldRow
                  label="Investment growth (above inflation)"
                  value={`${realGrowthRate >= 0 ? '+' : ''}${realGrowthRate.toFixed(1)}%`}
                  help={useSimulatedInflation
                    ? `Nominal growth varies by year (real + simulated CPIH)`
                    : `Nominal growth: ${(nominalGrowthRate * 100).toFixed(1)}%/yr`}
                >
                  <input
                    type="range" min="-15" max="15" step="0.1"
                    value={realGrowthRate}
                    onChange={(e) => setRealGrowthRate(Math.round(parseFloat(e.target.value) * 10) / 10)}
                    className="rc-slider"
                  />
                  <SliderTicks ticks={['-15%', '0%', '+15%']} />
                </FieldRow>
              </Disclosure>

              <Disclosure
                title="Custom drawdown years"
                summary={customDrawdownYearsSummary(incomeOverrides, drawdownYearsValidation)}
              >
                <IncomeOverridesEditor
                  overrides={incomeOverrides}
                  onChange={setIncomeOverrides}
                  lifeExpectancy={lifeExpectancy}
                  retirementAge={retirementAge}
                  defaultNet={withdrawalAmount}
                  validation={drawdownYearsValidation}
                />
              </Disclosure>

              <Disclosure
                title="Add defined benefit pension"
                summary={dbPensionEnabled
                  ? `£${Math.round(dbPensionAnnual).toLocaleString()}/yr from ${dbPensionAge}`
                  : 'Not included'}
              >
                <label className="flex items-start gap-2.5 py-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="rc-checkbox"
                    checked={dbPensionEnabled}
                    onChange={(e) => setDbPensionEnabled(e.target.checked)}
                  />
                  <span className="text-[13px] text-slate-700 leading-snug inline-flex items-start gap-1 flex-wrap">
                    <span>Enable defined benefit pension income</span>
                    <LabelHint
                      placement="below"
                      tooltipClassName="w-[min(300px,calc(100vw-2rem))]"
                      text="Adds a fixed annual income from your DB scheme from the age you set until your plan age. It is taxed on your record (like other pension income), appears on the income chart, and reduces how much you need to withdraw from your pot to hit your target net."
                    />
                  </span>
                </label>

                <div style={{ opacity: dbPensionEnabled ? 1 : 0.4, pointerEvents: dbPensionEnabled ? 'auto' : 'none' }}>
                  <p className="text-[13px] text-slate-700 leading-relaxed py-2">
                    Your DB pension is{' '}
                    <EditableAmount
                      size="sm"
                      value={`£${Math.round(dbPensionAnnual).toLocaleString()}/yr`}
                      rawValue={dbPensionAnnual}
                      onCommit={setDbPensionAnnual}
                      min={0}
                      max={200000}
                      suffix="/yr"
                    />
                    {' '}from age{' '}
                    <EditableNumber
                      value={String(dbPensionAge)}
                      rawValue={dbPensionAge}
                      onCommit={setDbPensionAge}
                    />
                    .
                  </p>
                </div>
              </Disclosure>

              <Disclosure
                title="State pension"
                summary={(() => {
                  if (!includeStatePensionInWithdrawal) return 'Not included';
                  const userPart = `£${Math.round(userStatePensionAnnual).toLocaleString()} from ${statePensionAge}`;
                  if (!includePartnerStatePension) {
                    return `${userPart}/yr`;
                  }
                  const partnerPart = `£${Math.round(partnerStatePensionAnnual).toLocaleString()} from ${partnerStatePensionAge}`;
                  if (statePensionAge === partnerStatePensionAge) {
                    return `${userPart} + ${partnerPart}/yr`;
                  }
                  return `${userPart} + partner ${partnerPart}`;
                })()}
              >
                <label className="flex items-start gap-2.5 py-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="rc-checkbox"
                    checked={includeStatePensionInWithdrawal}
                    onChange={(e) => setIncludeStatePensionInWithdrawal(e.target.checked)}
                  />
                  <span className="text-[13px] text-slate-700 leading-snug">
                    Include state pension in target net income
                  </span>
                </label>

                <div style={{ opacity: includeStatePensionInWithdrawal ? 1 : 0.4, pointerEvents: includeStatePensionInWithdrawal ? 'auto' : 'none' }}>
                  <p className="text-[13px] text-slate-700 leading-relaxed py-2">
                    Your state pension is{' '}
                    <EditableAmount
                      size="sm"
                      value={`£${Math.round(userStatePensionAnnual).toLocaleString()}/yr`}
                      rawValue={userStatePensionAnnual}
                      onCommit={setUserStatePensionAnnual}
                      min={0}
                      max={50000}
                      suffix="/yr"
                    />
                    {' '}from age{' '}
                    <EditableNumber
                      value={String(statePensionAge)}
                      rawValue={statePensionAge}
                      onCommit={setStatePensionAge}
                      min={STATE_PENSION_AGE_MIN}
                      max={STATE_PENSION_AGE_MAX}
                    />
                    .
                  </p>

                  <label className="flex items-start gap-2.5 py-2 cursor-pointer">
                    <input
                      type="checkbox"
                      className="rc-checkbox"
                      checked={includePartnerStatePension}
                      onChange={(e) => setIncludePartnerStatePension(e.target.checked)}
                    />
                    <span className="text-[13px] text-slate-700 leading-snug inline-flex items-start gap-1 flex-wrap">
                      <span>Include partner&apos;s state pension</span>
                      <LabelHint
                        placement="below"
                        tooltipClassName="w-[min(300px,calc(100vw-2rem))]"
                        text="Adds your partner's state pension to household net income and lowers pot withdrawals from state pension age. It is not included in your taxable income here — we assume it is taxed on their record."
                      />
                    </span>
                  </label>

                  <div style={{ opacity: includePartnerStatePension ? 1 : 0.4, pointerEvents: includePartnerStatePension ? 'auto' : 'none' }}>
                    <p className="text-[13px] text-slate-700 leading-relaxed py-2">
                      Partner&apos;s state pension is{' '}
                      <EditableAmount
                        size="sm"
                        value={`£${Math.round(partnerStatePensionAnnual).toLocaleString()}/yr`}
                        rawValue={partnerStatePensionAnnual}
                        onCommit={setPartnerStatePensionAnnual}
                        editable={includePartnerStatePension}
                        min={0}
                        max={50000}
                        suffix="/yr"
                      />
                      {' '}from age{' '}
                      <EditableNumber
                        value={String(partnerStatePensionAge)}
                        rawValue={partnerStatePensionAge}
                        onCommit={setPartnerStatePensionAge}
                        editable={includePartnerStatePension}
                        min={STATE_PENSION_AGE_MIN}
                        max={STATE_PENSION_AGE_MAX}
                      />
                      .
                    </p>
                  </div>
                </div>
              </Disclosure>

              <Disclosure
                title="Tax-free lump sum"
                summary={taxFreeOption === 'lumpsum' ? 'Take 25% at retirement' : 'Phased across withdrawals'}
              >
                <div className="space-y-1 pt-1">
                  <label className="flex items-start gap-2.5 py-2 cursor-pointer">
                    <input
                      type="radio" name="taxFreeOption" value="lumpsum"
                      className="rc-radio"
                      checked={taxFreeOption === 'lumpsum'}
                      onChange={(e) => setTaxFreeOption(e.target.value)}
                    />
                    <span className="text-[13px] text-slate-700 leading-snug">
                      Take as lump sum at retirement
                      {retirementData && taxFreeOption === 'lumpsum' && (() => {
                        const lumpSumAmount = taxFreeLumpSumAtRetirement;
                        const isCapped = potAtRetirementBeforeLumpSum * 0.25 > taxFreeLumpSumCap;
                        return (
                          <span className="block text-xs text-emerald-700 mt-1 tabular-nums">
                            Tax-free £{Math.round(lumpSumAmount).toLocaleString()}
                            {isCapped && <span className="text-slate-500 font-normal"> · capped at £{taxFreeLumpSumCap.toLocaleString()}</span>}
                          </span>
                        );
                      })()}
                    </span>
                  </label>
                  <label className="flex items-start gap-2.5 py-2 cursor-pointer">
                    <input
                      type="radio" name="taxFreeOption" value="phased"
                      className="rc-radio"
                      checked={taxFreeOption === 'phased'}
                      onChange={(e) => setTaxFreeOption(e.target.value)}
                    />
                    <span className="text-[13px] text-slate-700 leading-snug">
                      Phased — 25% tax-free on each withdrawal
                      {taxFreeOption === 'phased' && (
                        <span className="block text-xs text-slate-500 mt-1 tabular-nums">
                          Example: £{Math.round(year1PhasedGross * 0.25).toLocaleString()} tax-free + £{Math.round(year1PhasedGross * 0.75).toLocaleString()} taxable from pot
                        </span>
                      )}
                    </span>
                  </label>
                </div>
              </Disclosure>

              <Disclosure
                title="Annuity"
                titleHint={
                  <AnnuityDetailsHint
                    annuityPurchase={annuityPurchase}
                    annuityAnnualGross={annuityAnnualGross}
                    lifeExpectancy={lifeExpectancy}
                  />
                }
                summary={annuityEnabled && annuityPurchase > 0
                  ? `£${Math.round(annuityPurchase / 1000).toLocaleString()}k purchase · £${Math.round(annuityAnnualGross).toLocaleString()}/yr gross · level`
                  : 'Not included'}
              >
                <label className="flex items-start gap-2.5 py-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="rc-checkbox"
                    checked={annuityEnabled}
                    onChange={(e) => setAnnuityEnabled(e.target.checked)}
                  />
                  <span className="text-[13px] text-slate-700 leading-snug">
                    Buy annuity at retirement
                    <span className="block text-xs text-slate-500 mt-0.5">
                      Purchase from pot after (optional) tax-free lump sum; income counts toward your target net
                    </span>
                  </span>
                </label>

                <div style={{ opacity: annuityEnabled ? 1 : 0.4, pointerEvents: annuityEnabled ? 'auto' : 'none' }}>
                  <FieldRow
                    label="Purchase from pot"
                    value={`${formatAnnuityPurchasePercent(annuityPurchasePercentDisplay)}% · £${Math.round(annuityPurchase).toLocaleString()}`}
                    editable
                    rawValue={Math.round(annuityPurchase)}
                    onCommit={(amount) => {
                      const postLumpPot = potAtRetirementBeforeWithdrawals || 0;
                      if (postLumpPot <= 0) {
                        setAnnuityPurchaseAmountExact(0);
                        setAnnuityPurchasePercent(0);
                        return;
                      }
                      const clamped = Math.max(0, Math.min(Math.round(amount), postLumpPot));
                      setAnnuityPurchaseAmountExact(clamped);
                      setAnnuityPurchasePercent(
                        annuityPurchasePercentFromAmount(clamped, postLumpPot),
                      );
                    }}
                    min={0}
                    max={Math.max(potAtRetirementBeforeWithdrawals, 0)}
                  >
                    <input
                      type="range"
                      min="0"
                      max="100"
                      step="0.5"
                      value={annuityPurchaseSliderPercent}
                      onChange={(e) => {
                        const pct = parseFloat(e.target.value);
                        setAnnuityPurchaseAmountExact(null);
                        setAnnuityPurchasePercent(pct);
                      }}
                      className="rc-slider"
                    />
                    <SliderTicks
                      ticks={[
                        '0%',
                        potAtRetirementBeforeWithdrawals > 0
                          ? `£${Math.round(potAtRetirementBeforeWithdrawals / 2).toLocaleString()}`
                          : '50%',
                        potAtRetirementBeforeWithdrawals > 0
                          ? `£${Math.round(potAtRetirementBeforeWithdrawals).toLocaleString()}`
                          : '100%',
                      ]}
                    />
                  </FieldRow>

                  <FieldRow
                    label="Fixed annuity annual income"
                    value={`${formatAnnuityIncomeRatePercent(annuityIncomeRateDisplay)}% · £${Math.round(annuityAnnualGross).toLocaleString()}/yr`}
                    editable={annuityPurchase > 0}
                    rawValue={Math.round(annuityAnnualGross)}
                    onCommit={(amount) => {
                      if (annuityPurchase <= 0) {
                        setAnnuityAnnualGrossExact(0);
                        setAnnuityIncomeRatePercent(5);
                        return;
                      }
                      const clamped = Math.max(
                        annuityIncomeMinGross,
                        Math.min(annuityIncomeMaxGross, Math.round(amount)),
                      );
                      setAnnuityAnnualGrossExact(clamped);
                      setAnnuityIncomeRatePercent(
                        annuityIncomeRatePercentFromGross(clamped, annuityPurchase),
                      );
                    }}
                    min={Math.round(annuityIncomeMinGross)}
                    max={Math.round(annuityIncomeMaxGross)}
                    suffix="/yr"
                  >
                    <input
                      type="range"
                      min={ANNUITY_INCOME_RATE_MIN}
                      max={ANNUITY_INCOME_RATE_MAX}
                      step="0.1"
                      value={annuityIncomeSliderRate}
                      disabled={annuityPurchase <= 0}
                      onChange={(e) => {
                        const rate = parseFloat(e.target.value);
                        setAnnuityAnnualGrossExact(null);
                        setAnnuityIncomeRatePercent(rate);
                      }}
                      className="rc-slider"
                    />
                    <SliderTicks
                      ticks={[
                        annuityPurchase > 0
                          ? `2% · £${Math.round(annuityIncomeMinGross).toLocaleString()}`
                          : '2%',
                        annuityPurchase > 0
                          ? `6% · £${Math.round(annuityPurchase * 0.06).toLocaleString()}`
                          : '6%',
                        annuityPurchase > 0
                          ? `10% · £${Math.round(annuityIncomeMaxGross).toLocaleString()}`
                          : '10%',
                      ]}
                    />
                  </FieldRow>
                </div>
              </Disclosure>

              <Disclosure
                title="Cash buffer"
                titleHint={(
                  <LabelHint placement="below" tooltipClassName="w-[min(300px,calc(100vw-2rem))]">
                    <span className="flex flex-col gap-2">
                      <span>ISA or other savings held outside your pension pot — not included in pot value on the chart.</span>
                      <span className="text-slate-500">Counts toward your target net income as tax-free withdrawals. Before retirement, ISA can fund your target for as many years as your balance allows; after retirement it reduces what you need from your pension pot.</span>
                    </span>
                  </LabelHint>
                )}
                summary={cashBufferEnabled && cashBufferAmount > 0
                  ? (() => {
                    const modeLabel = cashBufferMode === CASH_BUFFER_MODE_BRIDGE
                      ? 'Bridge'
                      : cashBufferMode === CASH_BUFFER_MODE_TAX
                        ? 'Mitigate tax'
                        : 'Custom';
                    return `£${Math.round(cashBufferAmount / 1000).toLocaleString()}k · ${modeLabel} · age ${cashBufferSpreadStartModeEffective}–${cashBufferSpreadEndModeEffective}`;
                  })()
                  : 'Not included'}
              >
                <label className="flex items-start gap-2.5 py-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="rc-checkbox"
                    checked={cashBufferEnabled}
                    onChange={(e) => setCashBufferEnabled(e.target.checked)}
                  />
                  <span className="text-[13px] text-slate-700 leading-snug">
                    Use cash / ISA savings alongside pension
                    <span className="block text-xs text-slate-500 mt-0.5">
                      Separate from your pot — shown as its own depletion line on the chart
                    </span>
                  </span>
                </label>

                <div style={{ opacity: cashBufferEnabled ? 1 : 0.4, pointerEvents: cashBufferEnabled ? 'auto' : 'none' }}>
                  <FieldRow
                    label="ISA / cash savings"
                    value={`£${Math.round(cashBufferAmount).toLocaleString()}`}
                    editable
                    rawValue={Math.round(cashBufferAmount)}
                    onCommit={(amount) => setCashBufferAmount(Math.max(0, Math.min(CASH_BUFFER_MAX, Math.round(amount))))}
                    min={0}
                    max={CASH_BUFFER_MAX}
                  >
                    <input
                      type="range"
                      min={0}
                      max={CASH_BUFFER_LAYOUT.total}
                      step={1}
                      value={valueToIndex(CASH_BUFFER_LAYOUT, cashBufferAmount)}
                      onChange={(e) => setCashBufferAmount(indexToValue(CASH_BUFFER_LAYOUT, parseInt(e.target.value, 10)))}
                      className="rc-slider"
                    />
                    <SliderTicks ticks={['£0', '£50k', '£150k', '£500k']} />
                  </FieldRow>

                  <div className="mt-4">
                    <div className="text-[11px] font-semibold tracking-wider uppercase text-slate-500 mb-2">How to use it</div>
                    <div className="space-y-2">
                      <label className="flex items-start gap-2.5 cursor-pointer">
                        <input
                          type="radio"
                          name="cashBufferMode"
                          value={CASH_BUFFER_MODE_BRIDGE}
                          className="rc-radio"
                          checked={cashBufferMode === CASH_BUFFER_MODE_BRIDGE}
                          onChange={() => {
                            setCashBufferMode(CASH_BUFFER_MODE_BRIDGE);
                            const start = Math.max(currentAge + 1, retirementAge - cashBufferMaxPreRetYears);
                            const end = Math.max(start, retirementAge - 1);
                            setCashBufferSpreadStartAge(start);
                            setCashBufferSpreadEndAge(end);
                          }}
                        />
                        <span className="text-[13px] text-slate-700 leading-snug">
                          Use as a retirement bridge
                          <span className="block text-xs text-slate-500 mt-0.5">
                            Fund your target net income before retirement (flat pre-retirement), so you can stop work earlier.
                          </span>
                        </span>
                      </label>
                      <label className="flex items-start gap-2.5 cursor-pointer">
                        <input
                          type="radio"
                          name="cashBufferMode"
                          value={CASH_BUFFER_MODE_TAX}
                          className="rc-radio"
                          checked={cashBufferMode === CASH_BUFFER_MODE_TAX}
                          onChange={() => {
                            setCashBufferMode(CASH_BUFFER_MODE_TAX);
                            setCashBufferSpreadStartAge(retirementAge);
                            setCashBufferSpreadEndAge(cashBufferSpreadAgeMax);
                          }}
                        />
                        <span className="text-[13px] text-slate-700 leading-snug">
                          Use to mitigate tax
                          <span className="block text-xs text-slate-500 mt-0.5">
                            Aim for £0 tax where feasible by limiting pension withdrawals and topping up with ISA.
                          </span>
                          {cashBufferMode === CASH_BUFFER_MODE_TAX && (
                            <span className="block text-[11.5px] text-slate-500 mt-1 tabular-nums">
                              {zeroTaxYearsAchieved} year{zeroTaxYearsAchieved === 1 ? '' : 's'} at £0 tax (through age {lifeExpectancy})
                            </span>
                          )}
                        </span>
                      </label>
                      <label className="flex items-start gap-2.5 cursor-pointer">
                        <input
                          type="radio"
                          name="cashBufferMode"
                          value={CASH_BUFFER_MODE_CUSTOM}
                          className="rc-radio"
                          checked={cashBufferMode === CASH_BUFFER_MODE_CUSTOM}
                          onChange={() => setCashBufferMode(CASH_BUFFER_MODE_CUSTOM)}
                        />
                        <span className="text-[13px] text-slate-700 leading-snug">
                          Custom schedule
                          <span className="block text-xs text-slate-500 mt-0.5">
                            Choose the first and last ages your ISA is used.
                          </span>
                        </span>
                      </label>
                    </div>
                  </div>

                  {cashBufferMode === CASH_BUFFER_MODE_CUSTOM ? (
                    <FieldRow
                      label="Apply across years"
                      labelHint={(
                        <span className="flex flex-col gap-2 text-[13px] leading-relaxed">
                          <span>
                            The two handles set the first and last age your ISA or cash savings are used to help meet your
                            {' '}
                            <strong className="font-medium text-slate-800">target net income</strong>
                            . Outside this range, only your pension pot (plus state pension, DB pension, and annuity, if modelled) counts toward that target.
                          </span>
                          <span className="text-slate-500">
                            <strong className="font-medium text-slate-600">Before retirement:</strong>
                            {' '}
                            ISA pays your full target each year so you do not need to draw the pension yet. How many years
                            you can start before retirement depends on your ISA balance — the left handle cannot go further
                            back than your savings can sustain at your chosen target.
                          </span>
                          <span className="text-slate-500">
                            <strong className="font-medium text-slate-600">From retirement onward:</strong>
                            {' '}
                            ISA withdrawals are tax-free and reduce how much must come from the pension pot in the same year,
                            while your total take-home still aims at the target.
                          </span>
                        </span>
                      )}
                      value={`Age ${cashBufferSpreadStartEffective} – ${cashBufferSpreadEndEffective} (${cashBufferSpreadYearsEffective} yrs)`}
                    >
                    <div className="relative h-7 mb-1">
                      <div className="absolute w-full top-1/2 -translate-y-1/2 h-[3px] bg-slate-200 rounded-full pointer-events-none" />
                      <div
                        className="absolute top-1/2 -translate-y-1/2 h-[3px] bg-sky-500 rounded-full pointer-events-none"
                        style={{
                          left: `${cashBufferStartOffsetPct}%`,
                          width: `${Math.max(0, cashBufferEndOffsetPct - cashBufferStartOffsetPct)}%`,
                        }}
                      />
                      <input
                        type="range"
                        min={cashBufferOffsetMin}
                        max={cashBufferOffsetMax}
                        step={1}
                        value={cashBufferStartOffset}
                        onChange={(e) => {
                          const offset = parseInt(e.target.value, 10);
                          if (offset < cashBufferEndOffset) {
                            setCashBufferSpreadStartAge(retirementAge + offset);
                          }
                        }}
                        className="dual-range-slider"
                        style={{ position: 'absolute', width: '100%', top: 0, height: '1.75rem', zIndex: 4 }}
                      />
                      <input
                        type="range"
                        min={cashBufferOffsetMin}
                        max={cashBufferOffsetMax}
                        step={1}
                        value={cashBufferEndOffset}
                        onChange={(e) => {
                          const offset = parseInt(e.target.value, 10);
                          if (offset > cashBufferStartOffset) {
                            setCashBufferSpreadEndAge(retirementAge + offset);
                          }
                        }}
                        className="dual-range-slider"
                        style={{ position: 'absolute', width: '100%', top: 0, height: '1.75rem', zIndex: 5 }}
                      />
                    </div>
                    <SliderAxisTicks
                      items={(() => {
                        const leftPct = cashBufferOffsetToPct(cashBufferOffsetMin);
                        const midPct = cashBufferOffsetToPct(0);
                        const rightPct = cashBufferOffsetToPct(cashBufferOffsetMax);
                        const leftLabel = cashBufferOffsetMin === 0
                          ? 'Retirement'
                          : `${cashBufferOffsetMin} yrs`;
                        const ticks = [{ label: leftLabel, percent: leftPct }];
                        // Only render the central "Retirement" tick if it has clear
                        // breathing room from both extremes — otherwise it collides
                        // with the left or right label.
                        const MIN_GAP_PCT = 18;
                        if (
                          cashBufferOffsetMin !== 0
                          && midPct - leftPct >= MIN_GAP_PCT
                          && rightPct - midPct >= MIN_GAP_PCT
                        ) {
                          ticks.push({ label: 'Retirement', percent: midPct });
                        }
                        ticks.push({ label: `+${cashBufferOffsetMax} yrs`, percent: rightPct });
                        return ticks;
                      })()}
                    />
                    </FieldRow>
                  ) : (
                    <div className="mt-4 text-[12px] text-slate-600">
                      Using ISA from <span className="font-medium tabular-nums text-slate-900">{cashBufferSpreadStartModeEffective}</span> to{' '}
                      <span className="font-medium tabular-nums text-slate-900">{cashBufferSpreadEndModeEffective}</span>.
                      <button
                        type="button"
                        className="ml-2 text-slate-700 underline underline-offset-2 hover:text-slate-900"
                        onClick={() => setCashBufferMode(CASH_BUFFER_MODE_CUSTOM)}
                      >
                        Edit schedule
                      </button>
                    </div>
                  )}
                </div>
              </Disclosure>

            </div>
          </section>

          {/* ─── RESULTS ─────────────────────────────────────────────────── */}
          <section className="flex flex-col gap-6">

            {/* Hero */}
            <div>
              <div className="text-[13px] text-slate-500">
                At age {retirementAge}, in {yearsLeft} years, your pot will be worth
                {taxFreeOption === 'lumpsum' && taxFreeLumpSumAtRetirement > 0 && (
                  <span> after your tax-free lump sum</span>
                )}
              </div>
              <div className="mt-2 flex items-baseline flex-wrap" style={{ fontFamily: 'ui-serif, Georgia, "Times New Roman", serif' }}>
                <span className="text-[clamp(32px,7vw,48px)] font-normal text-slate-500 mr-1">£</span>
                <span className="text-[clamp(56px,12vw,88px)] font-normal tracking-tight tabular-nums leading-none text-slate-900" style={{ overflowWrap: 'anywhere' }}>
                  {Math.round(heroPotAtRetirement).toLocaleString()}
                </span>
              </div>
              <div className="mt-3.5 flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[12px] text-emerald-700 bg-emerald-50 font-medium tabular-nums">
                  £{Math.round(potAtRetirementReal).toLocaleString()} in today's money
                </span>
                {taxFreeOption === 'lumpsum' && taxFreeLumpSumAtRetirement > 0 && (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[12px] text-sky-800 bg-sky-50 font-medium tabular-nums">
                    −£{Math.round(taxFreeLumpSumAtRetirement).toLocaleString()} tax-free lump sum
                    {potAtRetirementBeforeLumpSum > 0 && (
                      <span className="font-normal text-sky-700/80">
                        {' '}({((taxFreeLumpSumAtRetirement / potAtRetirementBeforeLumpSum) * 100).toFixed(0)}% of pot
                        {potAtRetirementBeforeLumpSum * 0.25 > taxFreeLumpSumCap + 1 ? ' · lifetime cap' : ''})
                      </span>
                    )}
                  </span>
                )}
              </div>
            </div>

            {/* Safe-rate recommendation card */}

            {/* Chart */}
            <div className="bg-white border border-slate-200 rounded-2xl p-5">
              <div className="flex items-center justify-between mb-3 px-1">
                <div className="text-[11px] font-medium tracking-wider uppercase text-slate-500">
                  Projected value
                </div>
                {renderChartToolbar(false)}
              </div>
              {renderProjectionChart(420, 'chart')}
            </div>

            {chartFullscreenOpen && (
              <div
                className="fixed inset-0 z-[80] flex flex-col bg-stone-50 rc-fade-in"
                role="dialog"
                aria-modal="true"
                aria-label="Projected value chart full screen"
              >
                <div className="flex items-center justify-between gap-4 px-6 py-4 border-b border-slate-200 bg-white">
                  <div className="text-[11px] font-medium tracking-wider uppercase text-slate-500">
                    Projected value
                  </div>
                  {renderChartToolbar(true)}
                </div>
                <div className="flex-1 min-h-0 p-6">
                  {renderProjectionChart('100%', 'chart-fs')}
                </div>
              </div>
            )}

            {/* KPI strip */}
            <div className="grid grid-cols-1 sm:grid-cols-3 bg-white border border-slate-200 rounded-2xl overflow-visible">
              <Kpi
                className="border-b border-slate-200 sm:border-r"
                label="Withdrawal rate"
                value={`${withdrawalRate.toFixed(2)}%`}
                sub={`£${(year1GrossWithdrawal/1000).toFixed(0)}k gross draw of £${(potAtRetirementBeforeWithdrawals/1000).toFixed(0)}k pot`}
                tone={wrTone}
                toneText={wrText}
              />
              <Kpi
                className="border-b border-slate-200 sm:border-r"
                label="Funds last until"
                value={lastsToLifeExpectancy ? `Age ${lifeExpectancy}+` : `Age ${depletedAge}`}
                sub={lastsToLifeExpectancy ? `£${Math.round((data.find(d => d.age === lifeExpectancy)?.potEndNominal || 0)/1000).toLocaleString()}k at age ${lifeExpectancy}` : `${depletedAge - retirementAge}yr retirement`}
                tone={fundsTone}
                toneText={fundsTone === 'good' ? 'On track' : fundsTone === 'warn' ? 'Tight' : 'Short'}
              />
              <Kpi
                className="border-b border-slate-200"
                label="Lifetime tax"
                value={`£${Math.round(totalTax/1000)}k`}
                sub={`${effectiveTaxRate.toFixed(1)}% effective`}
                tone={taxTone}
                toneText={taxText}
              />
              <TaxFreeAllowanceKpi
                className="sm:col-span-2 sm:border-r border-t sm:border-t-0 border-slate-200"
                lifetimeCap={taxFreeLifetimeCap}
                potEntitlement={potTaxFreeEntitlement}
                usedInPlan={taxFreeUsedInPlan}
                unusedFromPot={taxFreeUnusedFromPot}
                usagePct={taxFreeUsagePct}
                capBindsPot={taxFreeCapBindsPot}
                taxFreeOption={taxFreeOption}
                exhaustionAge={taxFreeExhaustionAge}
                lifeExpectancy={lifeExpectancy}
              />
              <IncomeSplitKpi
                className="border-t border-slate-200"
                potSharePct={potIncomeSharePct}
                annuitySharePct={annuityIncomeSharePct}
                dbSharePct={dbIncomeSharePct}
                stateSharePct={stateIncomeSharePct}
                userStateSharePct={userStateIncomeSharePct}
                partnerStateSharePct={partnerStateIncomeSharePct}
                includePartnerStatePension={includePartnerStatePension}
                cashBufferSharePct={cashBufferIncomeSharePct}
                includeStatePension={includeStatePensionInWithdrawal}
                hasStateIncome={lifetimeGrossStatePension > 0}
                hasDbIncome={lifetimeGrossDbPension > 0}
                hasAnnuityIncome={lifetimeGrossAnnuity > 0}
                hasCashBufferIncome={lifetimeCashBuffer > 0}
              />
            </div>

            {effectiveIncomeOverrides.length > 0 && (
              <p className="text-[12px] text-slate-500 leading-relaxed -mt-2 mb-1" style={{ textWrap: 'pretty' }}>
                Includes custom drawdown years: {customDrawdownYearsSummary(incomeOverrides, drawdownYearsValidation)}.
                {' '}Sustainable net is the maximum main target that lasts alongside those custom years.
              </p>
            )}

            {/* Breakdown */}
            <div className="bg-white border border-slate-200 rounded-2xl p-5">
              <div className="text-[11px] font-medium tracking-wider uppercase text-slate-500 mb-4">
                Breakdown
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-[13px]">
                {(() => {
                  const inflationErosion = Math.max(0, potAtRetirementBeforeLumpSum - potAtRetirementReal);
                  const yearsToRetirement = retirementAge - currentAge;
                  const yearsInRetirement = Math.min(lifeExpectancy - retirementAge, withdrawalData.length);
                  const avgYearInRetirement = yearsToRetirement + (yearsInRetirement / 2);
                  const inflationToAvgYear = inflationFactorAtYearsFromNow(Math.round(avgYearInRetirement));
                  const growthDuringRetReal = cumulativeGrowthDuringRetirement / inflationToAvgYear;
                  return (
                    <React.Fragment>
                      <div className="flex items-baseline justify-between gap-3">
                        <span className="text-slate-500">Lifetime withdrawn from pot</span>
                        <span className="font-medium text-slate-900 tabular-nums">£{Math.round(totalWithdrawn).toLocaleString()}</span>
                      </div>
                      {lumpSumTaken > 0 && (
                        <div className="flex items-baseline justify-between gap-3">
                          <span className="text-slate-500">Tax-free lump sum at retirement</span>
                          <span className="font-medium text-sky-800 tabular-nums">£{Math.round(lumpSumTaken).toLocaleString()}</span>
                        </div>
                      )}
                      <div className="flex items-baseline justify-between gap-3">
                        <span className="text-slate-500">Lifetime net received</span>
                        <span className="font-medium text-slate-900 tabular-nums">£{Math.round(lifetimeNetReceived).toLocaleString()}</span>
                      </div>
                      {annuityEnabled && annuityPurchase > 0 && (
                        <div className="flex items-baseline justify-between gap-3">
                          <span className="text-slate-500">Annuity purchase (at retirement)</span>
                          <span className="font-medium text-violet-800 tabular-nums">£{Math.round(annuityPurchase).toLocaleString()}</span>
                        </div>
                      )}
                      {lifetimeGrossAnnuity > 0 && (
                        <div className="flex items-baseline justify-between gap-3">
                          <span className="text-slate-500">Lifetime annuity income</span>
                          <span className="font-medium text-violet-800 tabular-nums">£{Math.round(lifetimeGrossAnnuity).toLocaleString()}</span>
                        </div>
                      )}
                      {lifetimeGrossDbPension > 0 && (
                        <div className="flex items-baseline justify-between gap-3">
                          <span className="text-slate-500">Lifetime DB pension income</span>
                          <span className="font-medium text-teal-800 tabular-nums">£{Math.round(lifetimeGrossDbPension).toLocaleString()}</span>
                        </div>
                      )}
                      {cashBufferEnabled && cashBufferAmount > 0 && (
                        <div className="flex items-baseline justify-between gap-3">
                          <span className="text-slate-500">Cash buffer remaining</span>
                          <span className="font-medium text-sky-800 tabular-nums">£{Math.round(cashBufferBalanceAtPlanAge).toLocaleString()}</span>
                        </div>
                      )}
                      <div className="flex items-baseline justify-between gap-3">
                        <span className="text-slate-500">Growth during retirement</span>
                        <span className={`font-medium tabular-nums ${growthDuringRetReal >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                          {growthDuringRetReal >= 0 ? '+' : ''}£{Math.round(growthDuringRetReal).toLocaleString()}
                        </span>
                      </div>
                      <div className="flex items-baseline justify-between gap-3">
                        <span className="text-slate-500">Eroded by inflation</span>
                        <span className="font-medium text-slate-700 tabular-nums">−£{Math.round(inflationErosion).toLocaleString()}</span>
                      </div>
                    </React.Fragment>
                  );
                })()}
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* ─── Assumptions side panel ─────────────────────────────────────── */}
      <div
        className={`fixed top-0 right-0 h-full w-[360px] bg-white shadow-2xl border-l border-slate-200 transform transition-transform duration-300 ease-out z-50 ${
          assumptionsPanelOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="p-7 h-full overflow-y-auto">
          <div className="flex justify-between items-center mb-6">
            <div>
              <div className="text-[11px] font-semibold tracking-wider uppercase text-slate-500">Settings</div>
              <h2 className="text-[20px] font-medium tracking-tight text-slate-900 mt-0.5">Assumptions</h2>
            </div>
            <button
              type="button"
              onClick={() => setAssumptionsPanelOpen(false)}
              className="w-8 h-8 rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition flex items-center justify-center"
            >
              <svg width="12" height="12" viewBox="0 0 12 12"><path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
            </button>
          </div>

          <div className="space-y-7">
            {/* Tax */}
            <div>
              <h3 className="text-[11px] font-semibold tracking-wider uppercase text-slate-500 mb-3 pb-2 border-b border-slate-200">Tax & allowances</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs text-slate-600 mb-1.5">Personal Allowance</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">£</span>
                    <input
                      type="number"
                      value={personalAllowance}
                      onChange={(e) => {
                        const val = parseInt(e.target.value) || 0;
                        setPersonalAllowance(val);
                        setValidationErrors(prev => ({ ...prev, personalAllowance: '' }));
                      }}
                      onBlur={(e) => {
                        const val = parseInt(e.target.value) || 0;
                        if (val >= basicRateThreshold) {
                          setValidationErrors(prev => ({ ...prev, personalAllowance: `Must be less than Basic Rate Threshold (£${basicRateThreshold.toLocaleString()})` }));
                        } else if (val < 0) {
                          setValidationErrors(prev => ({ ...prev, personalAllowance: 'Must be a positive value' }));
                        }
                      }}
                      className={`rc-num-input pl-7 ${validationErrors.personalAllowance ? 'error' : ''}`}
                    />
                  </div>
                  {validationErrors.personalAllowance && (
                    <p className="text-[11px] text-rose-600 mt-1">{validationErrors.personalAllowance}</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs text-slate-600 mb-1.5">Basic Rate Threshold <span className="text-slate-400">(40% tax above)</span></label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">£</span>
                    <input
                      type="number"
                      value={basicRateThreshold}
                      onChange={(e) => {
                        const val = parseInt(e.target.value) || 0;
                        setBasicRateThreshold(val);
                        setValidationErrors(prev => ({ ...prev, basicRateThreshold: '' }));
                      }}
                      onBlur={(e) => {
                        const val = parseInt(e.target.value) || 0;
                        if (val <= personalAllowance) {
                          setValidationErrors(prev => ({ ...prev, basicRateThreshold: `Must exceed Personal Allowance (£${personalAllowance.toLocaleString()})` }));
                        } else if (val >= higherRateThreshold) {
                          setValidationErrors(prev => ({ ...prev, basicRateThreshold: `Must be less than Higher Rate Threshold (£${higherRateThreshold.toLocaleString()})` }));
                        }
                      }}
                      className={`rc-num-input pl-7 ${validationErrors.basicRateThreshold ? 'error' : ''}`}
                    />
                  </div>
                  {validationErrors.basicRateThreshold && (
                    <p className="text-[11px] text-rose-600 mt-1">{validationErrors.basicRateThreshold}</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs text-slate-600 mb-1.5">Higher Rate Threshold <span className="text-slate-400">(45% tax above)</span></label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">£</span>
                    <input
                      type="number"
                      value={higherRateThreshold}
                      onChange={(e) => {
                        const val = parseInt(e.target.value) || 0;
                        setHigherRateThreshold(val);
                        setValidationErrors(prev => ({ ...prev, higherRateThreshold: '' }));
                      }}
                      onBlur={(e) => {
                        const val = parseInt(e.target.value) || 0;
                        if (val <= basicRateThreshold) {
                          setValidationErrors(prev => ({ ...prev, higherRateThreshold: `Must exceed Basic Rate Threshold (£${basicRateThreshold.toLocaleString()})` }));
                        }
                      }}
                      className={`rc-num-input pl-7 ${validationErrors.higherRateThreshold ? 'error' : ''}`}
                    />
                  </div>
                  {validationErrors.higherRateThreshold && (
                    <p className="text-[11px] text-rose-600 mt-1">{validationErrors.higherRateThreshold}</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs text-slate-600 mb-1.5">Tax-Free Lump Sum Cap</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">£</span>
                    <input
                      type="number"
                      value={taxFreeLumpSumCap}
                      onChange={(e) => {
                        const val = parseInt(e.target.value) || 0;
                        setTaxFreeLumpSumCap(val);
                        setValidationErrors(prev => ({ ...prev, taxFreeLumpSumCap: '' }));
                      }}
                      onBlur={(e) => {
                        const val = parseInt(e.target.value) || 0;
                        if (val < 0) setValidationErrors(prev => ({ ...prev, taxFreeLumpSumCap: 'Must be a positive value' }));
                      }}
                      className={`rc-num-input pl-7 ${validationErrors.taxFreeLumpSumCap ? 'error' : ''}`}
                    />
                  </div>
                  {validationErrors.taxFreeLumpSumCap && (
                    <p className="text-[11px] text-rose-600 mt-1">{validationErrors.taxFreeLumpSumCap}</p>
                  )}
                  <p className="text-[11px] text-slate-500 mt-1">Maximum 25% tax-free amount</p>
                </div>
              </div>
            </div>

            {/* Pension access */}
            <div>
              <h3 className="text-[11px] font-semibold tracking-wider uppercase text-slate-500 mb-3 pb-2 border-b border-slate-200">Pension access</h3>
              <div>
                <label className="block text-xs text-slate-600 mb-1.5 inline-flex items-center">
                  Minimum pot access age
                  <LabelHint
                    placement="below"
                    text="Earliest age you can usually take money from a UK pension pot (currently 55, but this may rise). “You'll retire at” cannot be set below this age."
                  />
                </label>
                <input
                  type="number"
                  min={POT_ACCESS_AGE_MIN}
                  max={POT_ACCESS_AGE_MAX}
                  step="1"
                  value={potAccessAge}
                  onChange={(e) => {
                    const raw = parseInt(e.target.value, 10);
                    if (Number.isNaN(raw)) return;
                    const v = Math.min(POT_ACCESS_AGE_MAX, Math.max(POT_ACCESS_AGE_MIN, raw));
                    setPotAccessAge(v);
                    const minRet = Math.max(v, currentAge + 1);
                    if (retirementAge < minRet) setRetirementAge(minRet);
                  }}
                  onBlur={(e) => {
                    const raw = parseInt(e.target.value, 10);
                    const v = Number.isNaN(raw)
                      ? DEFAULT_POT_ACCESS_AGE
                      : Math.min(POT_ACCESS_AGE_MAX, Math.max(POT_ACCESS_AGE_MIN, raw));
                    setPotAccessAge(v);
                    const minRet = Math.max(v, currentAge + 1);
                    if (retirementAge < minRet) setRetirementAge(minRet);
                  }}
                  className="rc-num-input tabular-nums"
                />
                <p className="text-[11px] text-slate-500 mt-1.5">
                  UK normal minimum pension age ({POT_ACCESS_AGE_MIN}–{POT_ACCESS_AGE_MAX}); increase if rules change.
                </p>
              </div>
            </div>

            {/* State pension */}
            <div>
              <h3 className="text-[11px] font-semibold tracking-wider uppercase text-slate-500 mb-3 pb-2 border-b border-slate-200">State pension</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs text-slate-600 mb-1.5">Full State Pension (annual)</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">£</span>
                    <input
                      type="number"
                      value={fullStatePension}
                      onChange={(e) => {
                        const val = parseInt(e.target.value) || 0;
                        setFullStatePension(val);
                        setValidationErrors(prev => ({ ...prev, fullStatePension: '' }));
                      }}
                      onBlur={(e) => {
                        const val = parseInt(e.target.value) || 0;
                        if (val < 0) setValidationErrors(prev => ({ ...prev, fullStatePension: 'Must be a positive value' }));
                      }}
                      className={`rc-num-input pl-7 ${validationErrors.fullStatePension ? 'error' : ''}`}
                    />
                  </div>
                  {validationErrors.fullStatePension && (
                    <p className="text-[11px] text-rose-600 mt-1">{validationErrors.fullStatePension}</p>
                  )}
                  <p className="text-[11px] text-slate-500 mt-1">For 35 qualifying years</p>
                </div>
                <div>
                  <div className="flex items-baseline justify-between mb-1.5">
                    <label className="block text-xs text-slate-600">State Pension Age</label>
                    <span className="text-[13px] font-medium tabular-nums">{statePensionAge}</span>
                  </div>
                  <input
                    type="range"
                    min={STATE_PENSION_AGE_MIN}
                    max={STATE_PENSION_AGE_MAX}
                    step="1"
                    value={statePensionAge}
                    onChange={(e) => setStatePensionAge(parseInt(e.target.value, 10))}
                    className="rc-slider"
                  />
                  <SliderTicks ticks={[String(STATE_PENSION_AGE_MIN), '68', String(STATE_PENSION_AGE_MAX)]} />
                </div>
              </div>
            </div>

            {/* Longevity */}
            <div>
              <h3 className="text-[11px] font-semibold tracking-wider uppercase text-slate-500 mb-3 pb-2 border-b border-slate-200">Longevity</h3>
              <div>
                <div className="flex items-baseline justify-between mb-1.5">
                  <label className="block text-xs text-slate-600">Life Expectancy</label>
                  <span className="text-[13px] font-medium tabular-nums">{lifeExpectancy}</span>
                </div>
                <input
                  type="range" min="80" max="105" step="1"
                  value={lifeExpectancy}
                  onChange={(e) => setLifeExpectancy(parseInt(e.target.value))}
                  className="rc-slider"
                />
                <SliderTicks ticks={['80', '95', '105']} />
                <p className="text-[11px] text-slate-500 mt-1.5">Plan funds to last this long</p>
              </div>
            </div>

            <button
              type="button"
              onClick={() => {
                setPersonalAllowance(12570);
                setBasicRateThreshold(50270);
                setHigherRateThreshold(125140);
                setTaxFreeLumpSumCap(268275);
                setFullStatePension(12548);
                setStatePensionAge(68);
                setPartnerStatePensionAge(68);
                setLifeExpectancy(90);
                setPotAccessAge(DEFAULT_POT_ACCESS_AGE);
              }}
              className="w-full py-2.5 px-4 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-sm font-medium transition"
            >
              Reset to defaults
            </button>
          </div>
        </div>
      </div>

      {/* Overlay */}
      {assumptionsPanelOpen && (
        <div
          className="fixed inset-0 bg-slate-900/30 backdrop-blur-sm z-40"
          onClick={() => setAssumptionsPanelOpen(false)}
        />
      )}

      {/* Toast */}
      {showCopiedToast && (
        <div className="fixed top-6 right-6 z-[60] max-w-[320px] bg-slate-900 text-white px-4 py-3 rounded-2xl shadow-lg flex items-start gap-2.5 text-[13px] leading-snug rc-fade-in">
          <svg width="14" height="14" viewBox="0 0 16 16" className="flex-shrink-0 mt-0.5" aria-hidden="true">
            <path d="M2 8l4 4 8-8" stroke="#34d399" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
          </svg>
          <div>
            <div className="font-medium">Results link copied</div>
            <div className="text-slate-300 text-[12px] mt-0.5">
              Your pension inputs and assumptions are embedded in the URL.
            </div>
          </div>
        </div>
      )}

      <div className="max-w-[1280px] mx-auto px-10 pb-10">
        <SiteFooter />
      </div>
    </div>
  );
};

export default RetirementCalculator;
